import { formatMoney as formatShopifyMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const Astreal = {
  lastMenuTrigger: null,
  overlayStack: [],
  quickProduct: null,
  quickSelection: [],
  quickOptionMetadata: [],
  quickSizeGuideProfile: '',
  quickSizeOptionIndex: -1,
  quickFitSummary: '',
  quickRequestId: 0,
  quickPosterMedia: null,
  quickFlatlayMedia: null,
  cartBusy: false,
  cartStatusTimer: null,
  railPointer: null,
  railClickSuppressedUntil: 0,
  cartSurfaceRefreshQueue: Promise.resolve(),
  cartOptionEditor: null,
  privacyButtonOrderObserver: null,
  tickerMotionPreference: null,
  selectors: {
    themeToggle: '[data-astreal-theme-toggle]',
    ticker: '[data-astreal-ticker]',
    tickerMotionToggle: '[data-astreal-ticker-motion-toggle]',
    menuOpen: '[data-astreal-menu-open]',
    menuClose: '[data-astreal-menu-close]',
    currency: '[data-astreal-currency]',
  },

  getResolvedTheme() {
    return document.documentElement.dataset.astrealTheme === 'dark' ? 'dark' : 'light';
  },

  setTheme(theme, persist = true) {
    const resolvedTheme = theme === 'dark' ? 'dark' : 'light';
    const root = document.documentElement;
    root.dataset.astrealTheme = resolvedTheme;
    root.style.colorScheme = resolvedTheme;
    if (persist) {
      root.dataset.astrealThemeSource = 'saved';
      try {
        window.localStorage.setItem('astreal-theme', resolvedTheme);
      } catch (error) {
        // The theme still changes for this page when persistence is blocked.
      }
    }
    document.querySelectorAll(this.selectors.themeToggle).forEach((button) => {
      const isDark = resolvedTheme === 'dark';
      button.setAttribute('aria-pressed', String(isDark));
      button.setAttribute('aria-label', 'Dark theme');
      button.setAttribute('title', isDark ? 'Switch to light theme' : 'Switch to dark theme');
    });
    this.syncThemeImages(resolvedTheme);
  },

  setTickerMotion(motion, persist = false) {
    const resolvedMotion = motion === 'paused' ? 'paused' : 'playing';
    if (persist) {
      this.tickerMotionPreference = resolvedMotion;
      try { window.localStorage.setItem('astreal-ticker-motion', resolvedMotion); } catch (error) { /* The control still works for this page. */ }
    }
    const tickers = document.querySelectorAll(this.selectors.ticker);
    tickers.forEach((ticker) => {
      ticker.dataset.astrealTickerMotion = resolvedMotion;
    });
    document.querySelectorAll(this.selectors.tickerMotionToggle).forEach((button) => {
      const track = document.querySelector(`${this.selectors.ticker} .astreal-ticker__track`);
      if (!track) {
        button.hidden = true;
        return;
      }
      const label = resolvedMotion === 'playing' ? button.dataset.pauseLabel : button.dataset.playLabel;
      button.hidden = false;
      button.setAttribute('aria-controls', track.id);
      button.setAttribute('aria-label', label);
      button.textContent = label;
      button.title = label;
    });
  },

  initTickerMotion() {
    let preference = this.tickerMotionPreference;
    try {
      const saved = window.localStorage.getItem('astreal-ticker-motion');
      if (saved === 'playing' || saved === 'paused') preference = saved;
    } catch (error) {
      // Use the accessible device default when storage is unavailable.
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setTickerMotion(preference || (reduced ? 'paused' : 'playing'));
  },

  syncThemeImages(theme, scope = document) {
    const sourceKey = theme === 'dark' ? 'astrealDarkSrc' : 'astrealLightSrc';
    const sourceSetKey = theme === 'dark' ? 'astrealDarkSrcset' : 'astrealLightSrcset';
    scope.querySelectorAll('[data-astreal-theme-image]').forEach((image) => {
      if (image.dataset.astrealActiveTheme === theme) return;
      const source = image.dataset[sourceKey];
      const sourceSet = image.dataset[sourceSetKey];
      if (!source) return;
      if (sourceSet) image.srcset = sourceSet;
      else image.removeAttribute('srcset');
      image.src = source;
      image.dataset.astrealActiveTheme = theme;
    });
  },

  currencyPickerOptions(picker) {
    return [...(picker?.querySelectorAll('[data-astreal-currency-option]') || [])];
  },

  closeCurrencyPicker(picker, restoreFocus = false) {
    if (!picker) return;
    const trigger = picker.querySelector('[data-astreal-currency-trigger]');
    const options = picker.querySelector('[data-astreal-currency-options]');
    if (!trigger || !options || options.hidden) return;
    options.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    this.currencyPickerOptions(picker).forEach((option) => { option.tabIndex = -1; });
    if (restoreFocus) trigger.focus({ preventScroll: true });
  },

  closeCurrencyPickers(except = null) {
    document.querySelectorAll('[data-astreal-currency-picker]').forEach((picker) => {
      if (picker !== except) this.closeCurrencyPicker(picker);
    });
  },

  openCurrencyPicker(trigger) {
    const picker = trigger?.closest('[data-astreal-currency-picker]');
    const options = picker?.querySelector('[data-astreal-currency-options]');
    if (!picker || !options) return;
    this.closeCurrencyPickers(picker);
    options.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    const choices = this.currencyPickerOptions(picker);
    const selected = choices.find((option) => option.getAttribute('aria-selected') === 'true') || choices[0];
    choices.forEach((option) => { option.tabIndex = option === selected ? 0 : -1; });
    window.requestAnimationFrame(() => selected?.focus({ preventScroll: true }));
  },

  toggleCurrencyPicker(trigger) {
    const picker = trigger?.closest('[data-astreal-currency-picker]');
    const options = picker?.querySelector('[data-astreal-currency-options]');
    if (!picker || !options) return;
    if (options.hidden) this.openCurrencyPicker(trigger);
    else this.closeCurrencyPicker(picker, true);
  },

  selectCurrencyOption(option) {
    const picker = option?.closest('[data-astreal-currency-picker]');
    const input = picker?.querySelector('[data-astreal-currency-input]');
    const form = option?.form;
    const countryCode = option?.dataset.astrealCurrencyOption;
    if (!picker || !input || !form || !countryCode) return;
    if (input.value === countryCode) {
      this.closeCurrencyPicker(picker, true);
      return;
    }
    input.value = countryCode;
    this.currencyPickerOptions(picker).forEach((choice) => {
      choice.setAttribute('aria-selected', String(choice === option));
    });
    form.setAttribute('aria-busy', 'true');
    form.requestSubmit();
  },

  handleCurrencyKeydown(event) {
    const trigger = event.target.closest?.('[data-astreal-currency-trigger]');
    if (trigger && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.openCurrencyPicker(trigger);
      return true;
    }

    const option = event.target.closest?.('[data-astreal-currency-option]');
    const picker = (option || trigger)?.closest('[data-astreal-currency-picker]');
    if (!picker) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCurrencyPicker(picker, true);
      return true;
    }
    if (!option) return false;
    if (event.key === 'Tab') {
      event.preventDefault();
      const pickerTrigger = picker.querySelector('[data-astreal-currency-trigger]');
      this.closeCurrencyPicker(picker);
      const focusable = this.getFocusable(document);
      const triggerIndex = focusable.indexOf(pickerTrigger);
      const nextIndex = event.shiftKey ? triggerIndex - 1 : triggerIndex + 1;
      focusable[nextIndex]?.focus({ preventScroll: true });
      return true;
    }
    const choices = this.currencyPickerOptions(picker);
    const index = choices.indexOf(option);
    let nextIndex = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % choices.length;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + choices.length) % choices.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = choices.length - 1;
    else return false;
    event.preventDefault();
    choices.forEach((choice, choiceIndex) => { choice.tabIndex = choiceIndex === nextIndex ? 0 : -1; });
    choices[nextIndex]?.focus({ preventScroll: true });
    return true;
  },

  orderPrivacyButtons() {
    const manage = document.querySelector('#shopify-pc__banner__btn-manage-prefs');
    const decline = document.querySelector('#shopify-pc__banner__btn-decline');
    const accept = document.querySelector('#shopify-pc__banner__btn-accept');
    const controls = manage?.parentElement;
    if (!controls || decline?.parentElement !== controls || accept?.parentElement !== controls) return false;

    const orderedButtons = [manage, decline, accept];
    const currentButtons = [...controls.children].filter((element) => orderedButtons.includes(element));
    if (orderedButtons.some((button, index) => currentButtons[index] !== button)) {
      controls.append(...orderedButtons);
    }
    return true;
  },

  initPrivacyButtonOrder() {
    this.privacyButtonOrderObserver?.disconnect();
    this.privacyButtonOrderObserver = null;
    if (this.orderPrivacyButtons() || !('MutationObserver' in window)) return;

    const observer = new MutationObserver(() => {
      if (!this.orderPrivacyButtons()) return;
      observer.disconnect();
      if (this.privacyButtonOrderObserver === observer) this.privacyButtonOrderObserver = null;
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    this.privacyButtonOrderObserver = observer;
    window.setTimeout(() => {
      if (this.privacyButtonOrderObserver !== observer) return;
      observer.disconnect();
      this.privacyButtonOrderObserver = null;
    }, 30000);
  },

  setMenu(open, trigger = null) {
    const wasOpen = document.body.classList.contains('astreal-menu-open');
    if (open && trigger) this.lastMenuTrigger = trigger;
    document.documentElement.classList.toggle('astreal-menu-open', open);
    document.body.classList.toggle('astreal-menu-open', open);
    document.querySelectorAll('.astreal-header, .astreal-ticker, #MainContent, .page-wrapper > footer').forEach((element) => {
      element.inert = open;
    });
    document.querySelectorAll(this.selectors.menuOpen).forEach((button) => button.setAttribute('aria-expanded', String(open)));
    const menu = document.querySelector('#AstrealMobileMenu');
    if (!open && wasOpen) {
      const returnTarget = this.lastMenuTrigger && this.lastMenuTrigger.offsetParent !== null
        ? this.lastMenuTrigger
        : [...document.querySelectorAll(this.selectors.menuOpen)].find((button) => button.offsetParent !== null);
      returnTarget?.focus({ preventScroll: true });
      this.lastMenuTrigger = null;
    }
    if (menu) {
      menu.setAttribute('aria-hidden', String(!open));
      menu.inert = !open;
    }
    if (open) {
      menu?.querySelector('button, a, summary, select')?.focus();
    }
  },

  getFocusable(container) {
    return [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.tabIndex >= 0 && !element.hidden && element.offsetParent !== null && !element.closest('[inert]'));
  },

  trapMenuFocus(event) {
    if (event.key !== 'Tab' || !document.body.classList.contains('astreal-menu-open')) return;
    const menu = document.querySelector('#AstrealMobileMenu');
    if (!menu) return;
    const focusable = this.getFocusable(menu);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },

  syncOverlayState() {
    const top = this.overlayStack.at(-1)?.overlay;
    document.querySelectorAll('[data-astreal-overlay]').forEach((overlay) => {
      overlay.inert = overlay !== top;
    });
    const page = document.querySelector('.page-wrapper');
    if (page) page.inert = Boolean(top);
    document.documentElement.classList.toggle('astreal-overlay-open', Boolean(top));
    document.body.classList.toggle('astreal-overlay-open', Boolean(top));
    this.syncDialogViewport();
  },

  syncDialogViewport() {
    const height = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--astreal-dialog-height', `${height}px`);
    document.documentElement.classList.toggle('astreal-dialog-short', height < 520);
    this.layoutQuickActions();
  },

  layoutQuickActions() {
    const overlay = document.querySelector('[data-astreal-quick-add-dialog]');
    const actions = overlay?.querySelector('[data-astreal-quick-actions]');
    if (!actions) return;
    const destination = overlay.querySelector(window.matchMedia('(max-width: 899px)').matches
      ? '.astreal-quick-add__panel' : '[data-astreal-quick-form]');
    if (!destination || actions.parentElement === destination) return;
    const focused = actions.contains(document.activeElement) ? document.activeElement : null;
    destination.append(actions);
    focused?.focus({ preventScroll: true });
  },

  openOverlay(overlay, trigger) {
    if (!overlay || overlay.getAttribute('aria-hidden') === 'false') return;
    // Only guides/editors nest. Quick Add and cart never share a modal stack.
    if (overlay.matches('[data-astreal-quick-add-dialog], [data-astreal-cart-drawer]')) {
      while (this.overlayStack.length) this.closeOverlay();
    }
    this.setMenu(false);
    overlay.setAttribute('aria-hidden', 'false');
    overlay.inert = false;
    this.overlayStack.push({ overlay, trigger });
    this.syncOverlayState();
    window.requestAnimationFrame(() => {
      const panel = overlay.querySelector('[role="dialog"]') || overlay;
      const preferred = panel.querySelector('[autofocus]:not(:disabled)')
        || panel.querySelector('[data-astreal-overlay-close], button:not(:disabled), a, input:not(:disabled)');
      preferred?.focus();
    });
  },

  closeOverlay(overlay = this.overlayStack.at(-1)?.overlay) {
    const entry = this.overlayStack.at(-1);
    if (!entry || entry.overlay !== overlay) return;
    this.overlayStack.pop();
    const nextOverlay = this.overlayStack.at(-1)?.overlay;
    const page = document.querySelector('.page-wrapper');
    if (nextOverlay) nextOverlay.inert = false;
    else if (page) page.inert = false;
    const returnCandidates = [
      entry.trigger,
      ...(nextOverlay ? this.getFocusable(nextOverlay.querySelector('[role="dialog"]') || nextOverlay) : []),
      ...document.querySelectorAll('[data-astreal-cart-open], [data-astreal-menu-open], #MainContent a, #MainContent button'),
    ];
    const returnTarget = returnCandidates.find((candidate) => (
      candidate?.isConnected
      && candidate.offsetParent !== null
      && !candidate.disabled
      && candidate.tabIndex >= 0
      && !candidate.closest('[inert]')
    ));
    returnTarget?.focus({ preventScroll: true });
    if (entry.trigger?.getAttribute?.('aria-controls') === overlay.id) entry.trigger.setAttribute('aria-expanded', 'false');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.inert = true;
    if (overlay.matches('[data-astreal-size-guide-dialog]')) overlay.querySelector('[data-astreal-size-guide-mount]')?.replaceChildren();
    if (overlay.matches('[data-astreal-quick-add-dialog]')) {
      this.quickRequestId += 1;
      const status = overlay.querySelector('[data-astreal-quick-status]');
      if (status) status.textContent = '';
    }
    if (overlay.matches('[data-astreal-cart-drawer]')) this.cartStatus('');
    if (overlay.matches('[data-astreal-image-viewer]')) {
      const image = overlay.querySelector('[data-astreal-image-viewer-image]');
      const caption = overlay.querySelector('[data-astreal-image-viewer-caption]');
      image?.removeAttribute('src');
      if (image) image.alt = '';
      if (caption) {
        caption.textContent = '';
        caption.hidden = true;
      }
    }
    if (overlay.matches('[data-astreal-cart-option-editor]')) {
      overlay.querySelector('[data-astreal-cart-option-editor-options]')?.replaceChildren();
      const status = overlay.querySelector('[data-astreal-cart-option-editor-status]');
      if (status) status.textContent = '';
      this.cartOptionEditor = null;
    }
    this.syncOverlayState();
  },

  trapOverlayFocus(event) {
    if (event.key !== 'Tab') return;
    const top = this.overlayStack.at(-1)?.overlay;
    if (!top) return;
    const focusable = this.getFocusable(top.querySelector('[role="dialog"]') || top);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },

  formatMoney(cents, currency, showCurrencyCode = false) {
    const activeCurrency = currency || document.documentElement.dataset.astrealCurrency;
    const root = document.documentElement;
    const sampleCents = Number(root.dataset.astrealMoneySampleCents || 123456789);
    const amountSample = root.dataset.astrealMoneyAmountSample;
    const formattedSample = showCurrencyCode
      ? root.dataset.astrealMoneyWithCurrencySample
      : root.dataset.astrealMoneySample;
    const placeholders = [
      'amount',
      'amount_no_decimals',
      'amount_with_comma_separator',
      'amount_no_decimals_with_comma_separator',
      'amount_no_decimals_with_space_separator',
      'amount_with_space_separator',
      'amount_with_period_and_space_separator',
      'amount_with_apostrophe_separator',
    ];
    const placeholder = placeholders.find((candidate) => (
      formatShopifyMoney(sampleCents, `{{${candidate}}}`, activeCurrency) === amountSample
    ));
    const format = placeholder && amountSample && formattedSample?.includes(amountSample)
      ? formattedSample.replace(amountSample, `{{${placeholder}}}`)
      : '{{amount}} {{currency}}';
    return formatShopifyMoney(Number(cents || 0), format, activeCurrency);
  },

  productOptions(product) {
    return (product.options || []).map((option, index) => {
      const derivedValues = [...new Set((product.variants || []).map((variant) => this.variantOptions(variant)[index]).filter(Boolean))];
      if (typeof option === 'string') {
        return {
          name: option,
          position: index + 1,
          values: derivedValues,
        };
      }
      const providedValues = Array.isArray(option.values) ? option.values.filter((value) => value != null) : [];
      return {
        ...option,
        name: option.name || `Option ${index + 1}`,
        position: option.position || index + 1,
        values: providedValues.length ? providedValues : derivedValues,
      };
    });
  },

  variantOptions(variant) {
    if (Array.isArray(variant?.options)) return [...variant.options];
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  },

  variantForSelection(variants, selection) {
    return variants.find((variant) => this.variantOptions(variant).every((value, index) => value === selection[index]));
  },

  variantForOptionValue(variants, selection, optionIndex, value, validator = (variant) => variant.available) {
    const candidate = [...selection];
    candidate[optionIndex] = value;
    return variants.find((variant) => {
      if (!validator(variant)) return false;
      const options = this.variantOptions(variant);
      return options.slice(0, optionIndex + 1).every((optionValue, index) => candidate[index] == null || candidate[index] === '' || optionValue === candidate[index]);
    });
  },

  optionValueAvailable(variants, selection, optionIndex, value, validator = (variant) => variant.available) {
    return Boolean(this.variantForOptionValue(variants, selection, optionIndex, value, validator));
  },

  parseJsonScript(script, fallback = null) {
    if (!script) return fallback;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return fallback;
    }
  },

  colourValueMetadata(optionIndex, value) {
    return this.quickOptionMetadata?.[optionIndex]?.values?.find((entry) => entry.name === value) || null;
  },

  isRedundantMatteOption(option, values = []) {
    if (option?.isRedundantMatte) return true;
    const name = String(option?.name || '').trim().toLowerCase();
    const normalizedValues = values.map((value) => String(value?.name ?? value).trim().toLowerCase());
    return (name.includes('paper') || name.includes('finish'))
      && normalizedValues.length === 1
      && normalizedValues[0] === 'matte';
  },

  applyColourSwatch(button, metadata, value) {
    const visual = button.querySelector('.astreal-colour-swatch__visual');
    if (!visual) return;
    visual.style.removeProperty('background-image');
    visual.style.removeProperty('--astreal-colour-swatch-colour');
    visual.style.removeProperty('--astreal-colour-swatch-position');
    button.classList.remove('astreal-colour-swatch--unconfigured');
    if (metadata?.swatchImage) {
      visual.style.backgroundImage = `url("${String(metadata.swatchImage).replaceAll('"', '%22')}")`;
      visual.style.setProperty('--astreal-colour-swatch-colour', 'transparent');
      visual.style.setProperty('--astreal-colour-swatch-position', metadata.swatchPosition || 'center');
      return;
    }
    const colour = metadata?.swatchColour || value;
    if (colour && window.CSS?.supports?.('color', colour)) {
      visual.style.setProperty('--astreal-colour-swatch-colour', colour);
    } else {
      button.classList.add('astreal-colour-swatch--unconfigured');
    }
  },

  imageSource(value) {
    const source = typeof value === 'string' ? value : value?.src;
    if (!source) return '';
    return source.startsWith('//') ? `https:${source}` : source;
  },

  quantityRules(input, variant) {
    const rule = variant?.quantity_rule || {};
    input.min = rule.min || 1;
    input.step = rule.increment || 1;
    if (rule.max) input.max = rule.max;
    else input.removeAttribute('max');
    if (Number(input.value) < Number(input.min)) input.value = input.min;
  },

  async loadQuickAdd(trigger) {
    const overlay = document.querySelector('[data-astreal-quick-add-dialog]');
    if (!overlay) return;
    if (overlay.querySelector('[data-astreal-quick-form]')?.dataset.astrealSubmitting === 'true') return;
    this.cartStatus('');
    this.openOverlay(overlay, trigger);
    const loading = overlay.querySelector('[data-astreal-quick-loading]');
    const error = overlay.querySelector('[data-astreal-quick-error]');
    const content = overlay.querySelector('[data-astreal-quick-content]');
    const actions = overlay.querySelector('[data-astreal-quick-actions]');
    loading.hidden = false;
    error.hidden = true;
    content.hidden = true;
    actions.hidden = true;
    overlay.querySelector('[data-astreal-quick-status]').textContent = '';
    overlay.querySelector('[data-astreal-quantity-input]').value = '1';
    overlay.querySelector('.astreal-quick-add__information')?.removeAttribute('open');
    const requestId = ++this.quickRequestId;

    this.quickOptionMetadata = this.parseJsonScript(
      trigger.closest('.astreal-product-card')?.querySelector('[data-astreal-quick-option-metadata]'),
      [],
    );
    const card = trigger.closest('.astreal-product-card');
    this.quickFitSummary = this.parseJsonScript(card?.querySelector('[data-astreal-quick-fit-summary-copy]'), '');
    this.quickPosterMedia = this.parseJsonScript(card?.querySelector('[data-astreal-poster-media]'), null);
    this.quickShirtMedia = this.parseJsonScript(card?.querySelector('[data-astreal-shirt-media]'), null);
    this.quickFlatlayMedia = this.parseJsonScript(card?.querySelector('[data-astreal-flatlay-media]'), null);
    const buyingMedia = overlay.querySelector('[data-astreal-buying-media]');
    buyingMedia._astrealView = 'room';

    try {
      const url = new URL(trigger.dataset.productUrl, window.location.origin);
      if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname}.js`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('This product could not be loaded.');
      const product = await response.json();
      if (requestId !== this.quickRequestId) return;
      this.quickProduct = product;
      if (String(this.quickPosterMedia?.productId) !== String(product.id) || this.quickPosterMedia?.handle !== product.handle) this.quickPosterMedia = null;
      this.quickSizeGuideProfile = trigger.dataset.sizeGuideProfile || '';
      const explicitId = Number(url.searchParams.get('variant') || 0);
      const requestedId = explicitId || Number(trigger.dataset.variantId || 0);
      const selected = product.variants.find((variant) => Number(variant.id) === requestedId)
        || product.variants.find((variant) => variant.available)
        || product.variants[0];
      this.quickSelection = this.variantOptions(selected);
      this.quickSizeOptionIndex = /shirt/i.test(product.type || '')
        ? this.productOptions(product).findIndex((option) => /size/i.test(option.name)) : -1;
      if (this.quickSizeOptionIndex >= 0 && !explicitId) this.quickSelection[this.quickSizeOptionIndex] = null;
      this.renderQuickAdd(overlay, selected);
      loading.hidden = true;
      content.hidden = false;
      actions.hidden = false;
    } catch (loadError) {
      if (requestId !== this.quickRequestId) return;
      loading.hidden = true;
      error.textContent = loadError.message || 'This product could not be loaded.';
      error.hidden = false;
    }
  },

  renderQuickAdd(overlay, initialVariant) {
    const product = this.quickProduct;
    const form = overlay.querySelector('[data-astreal-quick-form]');
    if (form && form.dataset.astrealQuickControlsBound !== 'true') {
      form.dataset.astrealQuickControlsBound = 'true';
      form.addEventListener('click', (event) => {
        const option = event.target.closest('[data-astreal-quick-option-value]');
        const guide = event.target.closest('[data-astreal-size-guide-open]');
        const minus = event.target.closest('[data-astreal-quantity-minus]');
        const plus = event.target.closest('[data-astreal-quantity-plus]');
        if (!option && !guide && !minus && !plus) return;
        event.preventDefault();
        event.stopPropagation();
        if (option) this.selectQuickOption(option);
        else if (guide) this.openSizeGuide(guide);
        else this.changeQuantity(minus || plus, plus ? 1 : -1);
      }, true);
    }
    overlay.querySelector('[data-astreal-quick-title]').textContent = product.title;
    const fitSummary = overlay.querySelector('[data-astreal-quick-fit-summary]');
    fitSummary.textContent = this.quickFitSummary;
    fitSummary.hidden = !this.quickFitSummary;
    const link = overlay.querySelector('[data-astreal-quick-link]');
    link.href = product.url || new URL(overlay.dataset.productUrl || '/', window.location.origin).pathname;
    const optionsContainer = overlay.querySelector('[data-astreal-quick-options]');
    optionsContainer.replaceChildren();
    if (this.quickSizeGuideProfile === 'poster') {
      const note = document.createElement('p');
      note.className = 'astreal-product-option-note';
      note.textContent = 'Matte finish · Unframed · Made to order';
      optionsContainer.append(note);
    }

    this.productOptions(product).forEach((option, optionIndex) => {
      const values = (option.values || []).map((value) => typeof value === 'string' ? value : value.name);
      const lowerName = option.name.toLowerCase();
      const isSize = lowerName.includes('size');
      const isColour = Boolean(this.quickOptionMetadata?.[optionIndex]?.isColour) || lowerName.includes('color') || lowerName.includes('colour');
      const isDefault = lowerName === 'title' && values.length === 1 && values[0].toLowerCase() === 'default title';
      if (isDefault) return;
      if (this.isRedundantMatteOption(this.quickOptionMetadata?.[optionIndex] || option, values)) {
        if (this.quickSizeGuideProfile === 'poster') return;
        const note = document.createElement('p');
        note.className = 'astreal-product-option-note';
        note.textContent = 'Matte finish';
        optionsContainer.append(note);
        return;
      }

      const fieldset = document.createElement('fieldset');
      fieldset.className = `astreal-product-option${isColour ? ' astreal-product-option--colour' : ''}`;
      fieldset.dataset.astrealQuickOption = '';
      fieldset.dataset.optionIndex = String(optionIndex);
      const legend = document.createElement('legend');
      const label = document.createElement('span');
      label.textContent = option.name;
      if (isSize && this.quickSizeOptionIndex >= 0) {
        const prompt = document.createElement('span');
        prompt.className = 'astreal-size-prompt';
        prompt.dataset.astrealSizePrompt = '';
        prompt.textContent = ` · ${form.dataset.chooseSizeLabel}`;
        prompt.hidden = Boolean(this.quickSelection[optionIndex]);
        label.append(prompt);
      }
      if (isColour) {
        label.append(': ');
        const selectedValue = document.createElement('span');
        selectedValue.dataset.astrealQuickOptionSelection = '';
        selectedValue.textContent = this.quickSelection[optionIndex] || '';
        label.append(selectedValue);
      }
      legend.append(label);
      if (isSize && ['bella-canvas-3001', 'comfort-colors-1717', 'poster'].includes(this.quickSizeGuideProfile)) {
        const guide = document.createElement('button');
        guide.type = 'button';
        guide.className = 'astreal-size-guide-link';
        guide.dataset.astrealSizeGuideOpen = '';
        guide.dataset.sizeGuideProfile = this.quickSizeGuideProfile;
        guide.dataset.sizeValues = values.join('|');
        guide.dataset.quickOptionIndex = String(optionIndex);
        guide.setAttribute('aria-controls', 'AstrealSizeGuide');
        guide.setAttribute('aria-haspopup', 'dialog');
        guide.setAttribute('aria-expanded', 'false');
        guide.textContent = 'Size Guide';
        legend.append(guide);
      }
      fieldset.append(legend);
      const group = document.createElement('div');
      group.className = 'astreal-product-option__values';
      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.astrealQuickOptionValue = value;
        button.dataset.optionIndex = String(optionIndex);
        if (isColour) {
          button.className = 'astreal-colour-swatch';
          button.setAttribute('aria-label', `${option.name}: ${value}`);
          button.title = value;
          const visual = document.createElement('span');
          visual.className = 'astreal-colour-swatch__visual';
          visual.setAttribute('aria-hidden', 'true');
          const hiddenValue = document.createElement('span');
          hiddenValue.className = 'astreal-visually-hidden';
          hiddenValue.textContent = value;
          button.append(visual, hiddenValue);
          this.applyColourSwatch(button, this.colourValueMetadata(optionIndex, value), value);
        } else {
          button.textContent = value;
        }
        button.setAttribute('aria-pressed', String(this.quickSelection[optionIndex] === value));
        group.append(button);
      });
      fieldset.append(group);
      optionsContainer.append(fieldset);
    });
    this.updateQuickVariant(initialVariant);
  },

  refreshQuickAvailability() {
    const product = this.quickProduct;
    const overlay = document.querySelector('[data-astreal-quick-add-dialog]');
    overlay?.querySelectorAll('[data-astreal-quick-option]').forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);
      fieldset.querySelectorAll('[data-astreal-quick-option-value]').forEach((button) => {
        const available = this.optionValueAvailable(product.variants, this.quickSelection, index, button.dataset.astrealQuickOptionValue);
        button.disabled = !available;
        button.setAttribute('aria-pressed', String(button.dataset.astrealQuickOptionValue === this.quickSelection[index]));
      });
      const selection = fieldset.querySelector('[data-astreal-quick-option-selection]');
      if (selection) selection.textContent = this.quickSelection[index] || '';
      const prompt = fieldset.querySelector('[data-astreal-size-prompt]');
      if (prompt) prompt.hidden = Boolean(this.quickSelection[index]);
    });
  },

  selectQuickOption(button) {
    const fieldset = button.closest('[data-astreal-quick-option]');
    const index = Number(fieldset.dataset.optionIndex);
    const value = button.dataset.astrealQuickOptionValue;
    this.quickSelection[index] = value;
    if (this.quickSizeOptionIndex >= 0 && !this.quickSelection[this.quickSizeOptionIndex]) {
      this.refreshQuickAvailability();
      return;
    }
    const exactVariant = this.variantForSelection(this.quickProduct.variants, this.quickSelection);
    const variant = exactVariant?.available
      ? exactVariant
      : this.variantForOptionValue(this.quickProduct.variants, this.quickSelection, index, value);
    if (variant) {
      this.quickSelection = this.variantOptions(variant);
      this.updateQuickVariant(variant);
    }
    else this.refreshQuickAvailability();
  },

  updateQuickVariant(variant) {
    const overlay = document.querySelector('[data-astreal-quick-add-dialog]');
    if (!overlay || !variant) return;
    const currency = overlay.dataset.currency || document.documentElement.dataset.astrealCurrency;
    const showCurrencyCode = overlay.dataset.currencyCodeEnabled === 'true';
    const sizePending = this.quickSizeOptionIndex >= 0 && !this.quickSelection[this.quickSizeOptionIndex];
    overlay.querySelector('[data-astreal-quick-variant]').value = sizePending ? '' : variant.id;
    const detailsLink = overlay.querySelector('[data-astreal-quick-link]');
    const detailsUrl = new URL(this.quickProduct.url || detailsLink.href, window.location.origin);
    if (sizePending) detailsUrl.searchParams.delete('variant');
    else detailsUrl.searchParams.set('variant', String(variant.id));
    detailsLink.href = detailsUrl.href;
    const price = overlay.querySelector('[data-astreal-quick-price]');
    price.textContent = this.formatMoney(variant.price, currency, showCurrencyCode);
    if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
      const compare = document.createElement('s');
      compare.textContent = this.formatMoney(variant.compare_at_price, currency, showCurrencyCode);
      price.append(' ', compare);
    }
    const submit = overlay.querySelector('[data-astreal-quick-submit]');
    submit.disabled = sizePending || !variant.available || submit.form?.dataset.astrealSubmitting === 'true';
    submit.querySelector('[data-astreal-quick-submit-label]').textContent = sizePending
      ? submit.form.dataset.chooseSizeLabel : variant.available ? 'Add to cart' : 'Sold out';
    this.quantityRules(overlay.querySelector('[data-astreal-quantity-input]'), variant);

    const image = overlay.querySelector('[data-astreal-quick-image]');
    const missing = overlay.querySelector('[data-astreal-quick-image-missing]');
    const media = overlay.querySelector('[data-astreal-buying-media]');
    media.classList.remove('astreal-buying-media--shirt');
    if (this.updateBuyingMedia(media, this.quickPosterMedia, this.quickProduct, variant)) {
      missing.hidden = true;
      this.refreshQuickAvailability();
      return;
    }
    media.querySelector('[data-astreal-media-views]').hidden = true;
    media.querySelector('[data-astreal-media-caption]').hidden = true;
    if (this.updateShirtMedia(media, this.quickShirtMedia, this.quickProduct, variant)) {
      missing.hidden = true;
      this.refreshQuickAvailability();
      return;
    }
    const colourOption = this.productOptions(this.quickProduct).find((option) => /colou?r/i.test(option.name));
    const canUseFlatlay = this.quickFlatlayMedia && (!colourOption || colourOption.values?.length === 1);
    const source = canUseFlatlay ? this.quickFlatlayMedia.src : this.imageSource(variant.featured_image || this.quickProduct.featured_image);
    const link = media.querySelector('[data-astreal-buying-image-link]');
    link.hidden = !source;
    image.hidden = !source;
    missing.hidden = Boolean(source);
    image.removeAttribute('srcset');
    if (source) {
      image.src = source;
      image.alt = canUseFlatlay ? this.quickFlatlayMedia.alt : variant.featured_image?.alt || this.quickProduct.title;
      link.href = source;
      link.dataset.imageSrc = source;
      link.dataset.imageAlt = image.alt;
      link.setAttribute('aria-label', `Enlarge ${this.quickProduct.title} image`);
    } else {
      image.removeAttribute('src');
    }
    this.refreshQuickAvailability();
  },

  normalizeSize(value) {
    return String(value || '').toUpperCase().replaceAll(' ', '').replace(/^XXL$/, '2XL').replace(/^XXXL$/, '3XL').replace(/^XXXXL$/, '4XL').replace(/^XXXXXL$/, '5XL').replace(/^2X$/, '2XL').replace(/^3X$/, '3XL').replace(/^4X$/, '4XL').replace(/^5X$/, '5XL');
  },

  shirtAssetForVariant(mapping, product, variant) {
    if (!mapping || String(mapping.productId) !== String(product.id) || mapping.handle !== product.handle) return null;
    const colourIndex = this.productOptions(product).findIndex((option) => /^(color|colour)$/i.test(option.name));
    if (colourIndex < 0 || String(this.variantOptions(variant)[colourIndex]).trim().toLowerCase() !== mapping.colour?.toLowerCase()) return null;
    return mapping.studio?.src && mapping.studio.full ? mapping.studio : null;
  },

  updateShirtMedia(media, mapping, product, variant) {
    const asset = this.shirtAssetForVariant(mapping, product, variant);
    if (!media || !asset) return false;
    const image = media.querySelector('[data-astreal-buying-image]');
    const link = media.querySelector('[data-astreal-buying-image-link]');
    image.width = 1952;
    image.height = 1952;
    image.sizes = '(min-width: 900px) 550px, 100vw';
    image.srcset = asset.sources.map((source) => `${source.url} ${source.width}w`).join(', ');
    image.src = asset.src;
    image.alt = `${product.title}, ${mapping.colour} T-shirt shown from the front. Product illustration.`;
    image.hidden = false;
    link.hidden = false;
    link.href = asset.full;
    link.dataset.imageSrc = asset.full;
    link.dataset.imageAlt = `${product.title} · ${mapping.colour} · Front`;
    link.setAttribute('aria-label', `Enlarge ${product.title} shirt image`);
    media.classList.add('astreal-buying-media--shirt');
    return true;
  },

  normalizePosterSize(value) {
    return String(value || '').toLowerCase().replace(/["″“”'’]/g, '').replace(/\s/g, '').replace(/×/g, 'x');
  },

  posterSizeKey(product, variant) {
    const sizeIndex = this.productOptions(product).findIndex((option) => /size/i.test(option.name));
    return sizeIndex < 0 ? '' : this.normalizePosterSize(this.variantOptions(variant)[sizeIndex]);
  },

  updateBuyingMedia(media, mapping, product, variant) {
    if (!media || !mapping || String(mapping.productId) !== String(product.id) || mapping.handle !== product.handle) return false;
    const size = mapping.sizes?.[this.posterSizeKey(product, variant)];
    if (!size) return false;
    const view = media._astrealView === 'clean' ? 'clean' : 'room';
    const asset = size[view];
    if (!asset?.src || !asset.full) return false;
    const image = media.querySelector('[data-astreal-buying-image]');
    const link = media.querySelector('[data-astreal-buying-image-link]');
    const dimensions = `${size.inches.join(' × ')} in · ${size.cm.map((cm) => Number(cm).toFixed(1)).join(' × ')} cm`;
    image.width = 3000;
    const ratio = size.ratio.split(':').map(Number);
    image.height = view === 'room' ? 3000 : Math.round(3000 * ratio[1] / ratio[0]);
    image.srcset = asset.sources.map((source) => `${source.url} ${source.width}w`).join(', ');
    image.src = asset.src;
    image.alt = view === 'room' ? `${product.title} — ${dimensions}, room preview, approximate scale` : `${product.title} — clean ${size.ratio} artwork for ${dimensions}`;
    image.hidden = false;
    link.hidden = false;
    link.href = asset.full;
    link.dataset.imageSrc = asset.full;
    link.dataset.imageAlt = image.alt;
    link.setAttribute('aria-label', `Enlarge ${product.title} ${view === 'room' ? 'room preview' : 'artwork'}`);
    media.querySelector('[data-astreal-media-views]').hidden = false;
    media.querySelectorAll('[data-astreal-media-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.astrealMediaView === view)));
    const caption = media.querySelector('[data-astreal-media-caption]');
    caption.textContent = `${view === 'room' ? 'Room preview — approximate scale. ' : 'Clean artwork · '}${dimensions}`;
    caption.hidden = false;
    return true;
  },

  preparePosterGuide(guide, values, currentSize) {
    const sizes = String(values || '').split('|').filter(Boolean).map((size) => this.normalizePosterSize(size));
    const selected = this.normalizePosterSize(currentSize);
    let shown = 0;
    let selectedFound = false;
    guide.querySelectorAll('[data-poster-size]').forEach((row) => {
      row.hidden = !sizes.includes(row.dataset.posterSize);
      if (!row.hidden) shown += 1;
      const current = !row.hidden && selected === row.dataset.posterSize;
      if (current) {
        row.setAttribute('aria-current', 'true');
        selectedFound = true;
      } else row.removeAttribute('aria-current');
      row.querySelector('.astreal-poster-size-guide__selected').hidden = !current;
    });
    const note = guide.querySelector('[data-poster-size-unlisted]');
    if (note) note.hidden = shown > 0 && (!selected || selectedFound);
  },

  filterSizeGuide(guide, sizeValues = '') {
    if (!guide) return;
    const sizes = sizeValues.split('|').filter(Boolean).map((size) => this.normalizeSize(size));
    guide.querySelectorAll('[data-guide-size]').forEach((cell) => {
      cell.hidden = sizes.length > 0 && !sizes.includes(this.normalizeSize(cell.dataset.guideSize));
    });
  },

  openSizeGuide(trigger) {
    const overlay = document.querySelector('[data-astreal-size-guide-dialog]');
    if (!overlay) return;
    const mount = overlay.querySelector('[data-astreal-size-guide-mount]');
    const profile = trigger.dataset.sizeGuideProfile;
    if (!['bella-canvas-3001', 'comfort-colors-1717', 'poster'].includes(profile)) return;
    const template = overlay.querySelector(`[data-astreal-size-guide-template="${profile}"]`);
    if (!mount || !template) return;
    // Closing empties the mount; recreate it even when the same profile reopens.
    mount.innerHTML = template.innerHTML;
    mount.dataset.guideProfile = profile;
    overlay.dataset.guideProfile = profile;
    overlay.querySelector('[data-astreal-guide-kind]').textContent = profile === 'poster' ? 'Poster dimensions' : 'Garment measurements';
    if (profile === 'poster') {
      const currentSize = trigger.dataset.quickOptionIndex !== undefined
        ? this.quickSelection[Number(trigger.dataset.quickOptionIndex)]
        : trigger.closest('[data-astreal-option]')?.querySelector('[data-astreal-option-value][aria-pressed="true"]')?.dataset.astrealOptionValue;
      this.preparePosterGuide(mount, trigger.dataset.sizeValues, currentSize);
    } else {
      this.filterSizeGuide(mount, trigger.dataset.sizeValues || '');
      const selected = trigger.dataset.quickOptionIndex !== undefined
        ? this.quickSelection[Number(trigger.dataset.quickOptionIndex)]
        : trigger.closest('[data-astreal-option]')?.querySelector('[data-astreal-option-value][aria-pressed="true"]')?.dataset.astrealOptionValue;
      mount.querySelectorAll('[data-guide-size]').forEach((row) => {
        if (selected && this.normalizeSize(row.dataset.guideSize) === this.normalizeSize(selected)) row.setAttribute('aria-current', 'true');
      });
      this.initGuides(mount);
    }
    trigger.setAttribute('aria-expanded', 'true');
    this.openOverlay(overlay, trigger);
  },

  openInlineSizeGuide(trigger) {
    const targetId = trigger.getAttribute('aria-controls');
    const disclosure = targetId ? document.getElementById(targetId) : null;
    if (!disclosure) return;
    this.filterSizeGuide(disclosure, trigger.dataset.sizeValues || disclosure.dataset.sizeValues || '');
    const selected = trigger.closest('[data-astreal-option]')?.querySelector('[data-astreal-option-value][aria-pressed="true"]')?.dataset.astrealOptionValue;
    disclosure.querySelectorAll('[data-guide-size]').forEach((row) => {
      if (selected && this.normalizeSize(row.dataset.guideSize) === this.normalizeSize(selected)) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    });
    this.initGuides(disclosure);
    disclosure._astrealReturnTrigger = trigger;
    disclosure.open = true;
    const summary = disclosure.querySelector('summary');
    window.requestAnimationFrame(() => {
      disclosure.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      summary?.focus({ preventScroll: true });
    });
  },

  applyGuideUnit(guide, unit, announce = false) {
    guide.dataset.guideUnit = unit;
    guide.querySelectorAll('[data-astreal-guide-unit]').forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.astrealGuideUnit === unit)));
    guide.querySelectorAll('[data-astreal-guide-table]').forEach((table) => { table.hidden = table.dataset.astrealGuideTable !== unit; });
    guide.querySelectorAll('[data-guide-note]').forEach((note) => { note.hidden = note.dataset.guideNote !== unit; });
    const status = guide.querySelector('[data-guide-unit-status]');
    if (status && announce) status.textContent = unit === 'cm' ? guide.dataset.guideCmMessage : guide.dataset.guideInMessage;
  },

  initGuides(scope = document) {
    let saved;
    try { saved = window.localStorage.getItem('astreal-guide-unit'); } catch (error) { /* Use the market default. */ }
    scope.querySelectorAll('[data-astreal-size-guide-content]').forEach((guide) => this.applyGuideUnit(guide, ['cm', 'in'].includes(saved) ? saved : guide.dataset.guideDefaultUnit || 'in'));
  },

  setGuideUnit(button) {
    const guide = button.closest('[data-astreal-size-guide-content]');
    const unit = button.dataset.astrealGuideUnit;
    if (!guide || !['cm', 'in'].includes(unit)) return;
    try { window.localStorage.setItem('astreal-guide-unit', unit); } catch (error) { /* Page-local switching remains available. */ }
    document.querySelectorAll('[data-astreal-size-guide-content]').forEach((instance) => this.applyGuideUnit(instance, unit, instance === guide));
  },

  initProductRoots(scope = document) {
    scope.querySelectorAll('[data-astreal-product]').forEach((root) => {
      if (root.dataset.astrealReady === 'true') return;
      root.dataset.astrealReady = 'true';
      const nativeVariant = root.querySelector('[data-astreal-native-variant]');
      const enhancedVariant = root.querySelector('[data-astreal-variant-id]');
      if (nativeVariant && enhancedVariant) {
        nativeVariant.disabled = true;
        nativeVariant.removeAttribute('name');
        enhancedVariant.disabled = false;
        enhancedVariant.name = 'id';
      }
      try {
        root._astrealVariants = JSON.parse(root.querySelector('[data-astreal-product-variants]').textContent);
      } catch (error) {
        root._astrealVariants = [];
      }
      const selectedId = Number(root.querySelector('[data-astreal-variant-id]')?.value || 0);
      const selected = root._astrealVariants.find((variant) => Number(variant.id) === selectedId) || root._astrealVariants[0];
      root._astrealSelection = this.variantOptions(selected);
      root._astrealSizeOptionIndex = Number(root.dataset.shirtSizeIndex ?? -1);
      if (root.dataset.requireSizeChoice === 'true' && root._astrealSizeOptionIndex >= 0) root._astrealSelection[root._astrealSizeOptionIndex] = null;
      root._astrealPosterMedia = this.parseJsonScript(root.querySelector('[data-astreal-poster-media]'), null);
      root._astrealShirtMedia = this.parseJsonScript(root.querySelector('[data-astreal-shirt-media]'), null);
      this.updateProductVariant(root);
      const inlineSizeGuide = root.querySelector('[data-astreal-product-size-guide]');
      if (inlineSizeGuide) this.filterSizeGuide(inlineSizeGuide, inlineSizeGuide.dataset.sizeValues || '');

      const stage = root.querySelector('[data-astreal-gallery-stage]');
      if (stage) {
        let startX = 0;
        stage.addEventListener('touchstart', (event) => { startX = event.changedTouches[0].clientX; }, { passive: true });
        stage.addEventListener('touchend', (event) => {
          const distance = event.changedTouches[0].clientX - startX;
          if (Math.abs(distance) < 45) return;
          stage.dataset.astrealGallerySwiped = 'true';
          window.setTimeout(() => { delete stage.dataset.astrealGallerySwiped; }, 500);
          const ids = [...root.querySelectorAll('[data-astreal-gallery-media]')].map((item) => item.dataset.astrealGalleryMedia);
          const active = root.querySelector('[data-astreal-gallery-media]:not([hidden])')?.dataset.astrealGalleryMedia;
          const current = Math.max(0, ids.indexOf(active));
          const next = distance < 0 ? Math.min(ids.length - 1, current + 1) : Math.max(0, current - 1);
          this.setGalleryMedia(root, ids[next]);
        }, { passive: true });
      }
    });
  },

  refreshProductAvailability(root) {
    root.querySelectorAll('[data-astreal-option]').forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);
      fieldset.querySelectorAll('[data-astreal-option-value]').forEach((button) => {
        const available = this.optionValueAvailable(root._astrealVariants, root._astrealSelection, index, button.dataset.astrealOptionValue);
        button.disabled = !available;
        button.setAttribute('aria-pressed', String(root._astrealSelection[index] === button.dataset.astrealOptionValue));
      });
      const selection = fieldset.querySelector('[data-astreal-option-selection]');
      if (selection) selection.textContent = root._astrealSelection[index] || '';
      const prompt = fieldset.querySelector('[data-astreal-size-prompt]');
      if (prompt) prompt.hidden = Boolean(root._astrealSelection[index]);
    });
  },

  selectProductOption(button) {
    const root = button.closest('[data-astreal-product]');
    const fieldset = button.closest('[data-astreal-option]');
    const index = Number(fieldset.dataset.optionIndex);
    const value = button.dataset.astrealOptionValue;
    root._astrealSelection[index] = value;
    if (root._astrealSizeOptionIndex >= 0 && !root._astrealSelection[root._astrealSizeOptionIndex]) {
      this.updateProductVariant(root);
      return;
    }
    const exactVariant = this.variantForSelection(root._astrealVariants, root._astrealSelection);
    const variant = exactVariant?.available
      ? exactVariant
      : this.variantForOptionValue(root._astrealVariants, root._astrealSelection, index, value);
    if (variant) {
      root._astrealSelection = this.variantOptions(variant);
      this.updateProductVariant(root, true);
    } else {
      this.refreshProductAvailability(root);
    }
  },

  updateProductVariant(root, changeUrl = false) {
    const variant = this.variantForSelection(root._astrealVariants, root._astrealSelection);
    if (!variant) {
      root.querySelector('[data-astreal-variant-id]').value = '';
      const submit = root.querySelector('[data-astreal-add-to-cart]');
      submit.disabled = true;
      const labels = root.querySelector('[data-astreal-purchase-labels]');
      submit.querySelector('[data-astreal-add-label]').textContent = labels.dataset.chooseSizeLabel;
      this.refreshProductAvailability(root);
      return;
    }
    root.querySelector('[data-astreal-variant-id]').value = variant.id;
    const currency = root.dataset.currency || document.documentElement.dataset.astrealCurrency;
    const showCurrencyCode = root.dataset.currencyCodeEnabled === 'true';
    root.querySelector('[data-astreal-product-price]').textContent = this.formatMoney(variant.price, currency, showCurrencyCode);
    const compare = root.querySelector('[data-astreal-product-compare]');
    compare.hidden = !(variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price));
    if (!compare.hidden) compare.textContent = this.formatMoney(variant.compare_at_price, currency, showCurrencyCode);
    const submit = root.querySelector('[data-astreal-add-to-cart]');
    submit.disabled = !variant.available || submit.form?.dataset.astrealSubmitting === 'true';
    submit.querySelector('[data-astreal-add-label]').textContent = variant.available ? 'Add to cart' : 'Sold out';
    this.quantityRules(root.querySelector('[data-astreal-quantity-input]'), variant);
    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    const posterMedia = root._astrealPosterMedia;
    const productIdentity = { id: root.dataset.productId, handle: root.dataset.productHandle, title: root.querySelector('h1')?.textContent || '', options: this.parseJsonScript(root.querySelector('[data-astreal-product-option-metadata]'), []) };
    const shirtMedia = root.querySelector('[data-astreal-shirt-buying-media]');
    let shirtMapped = false;
    if (shirtMedia) {
      shirtMapped = this.updateShirtMedia(shirtMedia, root._astrealShirtMedia, productIdentity, variant);
      shirtMedia.hidden = !shirtMapped;
      const gallery = shirtMedia.closest('.astreal-product-gallery');
      if (gallery) gallery.hidden = !shirtMapped;
      const nativeMedia = root.querySelector('[data-astreal-shirt-native-media]');
      if (nativeMedia) nativeMedia.hidden = shirtMapped;
    }
    if (!shirtMapped && !this.updateBuyingMedia(root.querySelector('[data-astreal-buying-media]'), posterMedia, productIdentity, variant) && mediaId) this.setGalleryMedia(root, String(mediaId));
    this.refreshProductAvailability(root);
    if (changeUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  },

  setGalleryMedia(root, mediaId) {
    if (!mediaId) return;
    root.querySelectorAll('[data-astreal-gallery-media]').forEach((item) => {
      const active = item.dataset.astrealGalleryMedia === String(mediaId);
      item.hidden = !active;
      if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
    });
    root.querySelectorAll('[data-astreal-gallery-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.astrealGalleryTarget === String(mediaId))));
  },

  openImageViewer(trigger) {
    const overlay = document.querySelector('[data-astreal-image-viewer]');
    const image = overlay?.querySelector('[data-astreal-image-viewer-image]');
    const source = trigger?.dataset.imageSrc;
    if (!overlay || !image || !source) return;
    const alt = trigger.dataset.imageAlt || 'Product image';
    overlay.classList.toggle('astreal-image-viewer--shirt', Boolean(trigger.closest('.astreal-buying-media--shirt')));
    const caption = overlay.querySelector('[data-astreal-image-viewer-caption]');
    image.src = source;
    image.alt = '';
    if (caption) {
      caption.textContent = alt;
      caption.hidden = false;
    }
    this.openOverlay(overlay, trigger);
  },

  changeQuantity(button, direction) {
    const control = button.closest('.astreal-quantity__control');
    const input = control?.querySelector('[data-astreal-quantity-input]');
    if (!input) return;
    const step = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    input.value = String(Math.min(max, Math.max(min, Number(input.value || min) + (direction * step))));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  },

  async addToCart(form) {
    if (form.dataset.astrealSubmitting === 'true') return;
    const isQuick = form.matches('[data-astreal-quick-form]');
    const purchaseScope = isQuick ? form.closest('[data-astreal-quick-add-dialog]') : form;
    const button = purchaseScope.querySelector(isQuick ? '[data-astreal-quick-submit]' : '[data-astreal-add-to-cart]');
    const label = button?.querySelector(isQuick ? '[data-astreal-quick-submit-label]' : '[data-astreal-add-label]');
    const status = purchaseScope.querySelector(isQuick ? '[data-astreal-quick-status]' : '[data-astreal-add-status]');
    const variantInput = form.querySelector(isQuick ? '[data-astreal-quick-variant]' : '[data-astreal-variant-id]');
    if (!variantInput?.value) {
      const labels = isQuick ? form : form.querySelector('[data-astreal-purchase-labels]');
      if (status) status.textContent = labels.dataset.chooseSizeError;
      const root = form.closest('[data-astreal-product]');
      const index = isQuick ? this.quickSizeOptionIndex : root?._astrealSizeOptionIndex;
      purchaseScope.querySelector(`[data-option-index="${index}"] [data-astreal-${isQuick ? 'quick-option' : 'option'}-value]:not(:disabled)`)?.focus();
      return;
    }
    const resetLabel = label?.textContent || 'Add to cart';
    const payload = new FormData(form);
    const controls = [...form.querySelectorAll('button, input, select')];
    if (button && !controls.includes(button)) controls.push(button);
    const disabledStates = controls.map((control) => control.disabled);
    const quickOverlay = isQuick ? form.closest('[data-astreal-quick-add-dialog]') : null;
    const returnTrigger = isQuick
      ? this.overlayStack.find((entry) => entry.overlay === quickOverlay)?.trigger || button
      : button;
    form.dataset.astrealSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');
    controls.forEach((control) => { control.disabled = true; });
    if (status) status.textContent = '';
    this.cartStatus('');
    if (label) label.textContent = form.dataset.addingLabel || 'Adding…';
    try {
      const endpoint = new URL(form.action, window.location.origin);
      if (!endpoint.pathname.endsWith('.js')) endpoint.pathname = `${endpoint.pathname}.js`;
      const response = await fetch(endpoint, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: payload });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');
      if (label) label.textContent = 'Added ✓';
      if (status) status.textContent = `${result.product_title || 'Item'} added to cart.`;
      await this.refreshCartCount();
      window.dispatchEvent(new CustomEvent('astreal:cart:updated', { detail: result }));
      controls.forEach((control, index) => { control.disabled = disabledStates[index]; });
      if (quickOverlay && this.overlayStack.at(-1)?.overlay === quickOverlay) this.closeOverlay(quickOverlay);
      await this.openCartDrawer(returnTrigger);
    } catch (error) {
      if (status) status.textContent = error.message || 'This item could not be added.';
      const variantId = Number(form.querySelector(isQuick ? '[data-astreal-quick-variant]' : '[data-astreal-variant-id]')?.value || 0);
      const productRoot = form.closest('[data-astreal-product]');
      const variants = isQuick ? this.quickProduct?.variants : productRoot?._astrealVariants;
      button.disabled = !variants?.find((variant) => Number(variant.id) === variantId)?.available;
    } finally {
      delete form.dataset.astrealSubmitting;
      form.removeAttribute('aria-busy');
      controls.forEach((control, index) => { control.disabled = disabledStates[index]; });
      const variantId = Number(form.querySelector(isQuick ? '[data-astreal-quick-variant]' : '[data-astreal-variant-id]')?.value || 0);
      const variants = isQuick ? this.quickProduct?.variants : form.closest('[data-astreal-product]')?._astrealVariants;
      const available = Boolean(variants?.find((variant) => Number(variant.id) === variantId)?.available);
      if (button) button.disabled = !available;
      if (label) label.textContent = available ? resetLabel : 'Sold out';
    }
  },

  async fetchCart() {
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    const quick = document.querySelector('[data-astreal-quick-add-dialog]');
    const response = await fetch(drawer?.dataset.cartJsonUrl || quick?.dataset.cartJsonUrl || '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The cart could not be loaded.');
    return response.json();
  },

  async fetchCartPresentation() {
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    const page = document.querySelector('[data-astreal-cart-page]');
    const url = page?.dataset.cartSectionUrl || drawer?.dataset.cartSectionUrl;
    if (!url) return null;
    const response = await fetch(url, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The cart display could not be refreshed.');
    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const replacement = template.content.querySelector('[data-astreal-cart-page]');
    if (!replacement) throw new Error('The cart display could not be refreshed.');
    return replacement;
  },

  updateCartIndicators(cart) {
    const quantity = Number(cart.item_count) || 0;
    document.querySelectorAll('[data-astreal-cart-count]').forEach((count) => {
      count.textContent = quantity > 99 ? '99+' : String(quantity);
      count.hidden = quantity === 0;
    });
    document.querySelectorAll('[data-astreal-cart-open]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Cart with ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
    });
  },

  merchandiseSubtotal(cart, beforeDiscounts = false) {
    const fallback = Number(beforeDiscounts ? cart?.original_total_price : cart?.total_price) || 0;
    if (!Array.isArray(cart?.items)) return fallback;

    // Checkout/session totals can include delivery. Merchandise totals must not.
    const amounts = cart.items.map((item) => Number(
      beforeDiscounts ? item.original_line_price ?? item.line_price : item.final_line_price ?? item.line_price,
    ));
    if (amounts.some((amount) => !Number.isFinite(amount))) return fallback;
    const lineSubtotal = amounts.reduce((sum, amount) => sum + amount, 0);
    const merchandiseDiscount = beforeDiscounts ? 0 : (cart.cart_level_discount_applications || [])
      .filter((discount) => discount.target_type === 'line_item')
      .reduce((sum, discount) => sum + (Number(discount.total_allocated_amount) || 0), 0);
    return Math.max(0, lineSubtotal - merchandiseDiscount);
  },

  updateFreeShippingProgress(cart, scope = document, announce = false) {
    const currency = String(cart?.currency || document.documentElement.dataset.astrealCurrency || '').toUpperCase();
    const totalAfterDiscounts = this.merchandiseSubtotal(cart);
    const totalBeforeDiscounts = this.merchandiseSubtotal(cart, true);
    let announcement = '';

    scope.querySelectorAll('[data-astreal-free-shipping-progress]').forEach((meter) => {
      const enabled = meter.dataset.enabled === 'true';
      meter.hidden = !enabled;
      if (!enabled) return;

      const usdThreshold = Number(meter.dataset.thresholdUsdCents || 0);
      const threshold = currency === 'USD' ? usdThreshold : 0;
      const country = String(meter.dataset.country || '').trim().toUpperCase();
      const eligibleCountries = String(meter.dataset.eligibleCountries || '')
        .split(/[;,]/)
        .map((value) => value.trim().toUpperCase())
        .filter(Boolean);
      const marketEligibilityConfirmed = meter.dataset.marketEligibilityConfirmed === 'true';
      const allProductsEligible = meter.dataset.allProductsEligible === 'true';
      const offerScopeConfirmed = usdThreshold > 0 && marketEligibilityConfirmed && allProductsEligible && eligibleCountries.includes(country);
      const eligibilityKnown = currency === 'USD' && threshold > 0 && offerScopeConfirmed;
      const subtotal = meter.dataset.subtotalBasis === 'before_discounts' ? totalBeforeDiscounts : totalAfterDiscounts;
      const remaining = Math.max(0, threshold - subtotal);
      const unlocked = eligibilityKnown && remaining === 0;
      let message = 'Shipping costs and free-shipping eligibility are confirmed at checkout.';
      if (eligibilityKnown) {
        message = unlocked
          ? meter.dataset.unlockedMessage || `Free standard shipping unlocked for ${country}.`
          : `Add ${this.formatMoney(remaining, currency, true)} to unlock free standard shipping.`;
      } else if (offerScopeConfirmed) {
        const thresholdAmount = usdThreshold / 100;
        const thresholdLabel = meter.dataset.thresholdUsdLabel
          || `$${Number.isInteger(thresholdAmount) ? thresholdAmount : thresholdAmount.toFixed(2)} USD`;
        message = `Free standard shipping starts at ${thresholdLabel} or Shopify’s live local-currency equivalent. Checkout confirms eligibility.`;
      }
      const percent = eligibilityKnown
        ? (unlocked ? 100 : Math.max(0, Math.min(99, Math.floor((subtotal / threshold) * 100))))
        : 0;
      const text = meter.querySelector('[data-astreal-free-shipping-text]');
      const track = meter.querySelector('[data-astreal-free-shipping-bar-track]');
      const bar = meter.querySelector('[data-astreal-free-shipping-bar]');

      if (text) text.textContent = message;
      if (track) {
        track.hidden = !eligibilityKnown;
        track.setAttribute('aria-valuenow', String(percent));
        track.setAttribute('aria-valuetext', message);
      }
      if (bar) bar.style.setProperty('--astreal-free-shipping-percent', `${percent}%`);
      meter.dataset.currency = currency;
      meter.dataset.state = eligibilityKnown ? (unlocked ? 'unlocked' : 'progress') : (offerScopeConfirmed ? 'checkout-confirmed' : 'neutral');
      announcement = message;
    });

    if (announce && announcement) {
      const liveRegion = document.querySelector('[data-astreal-free-shipping-announcer]');
      if (liveRegion && liveRegion.dataset.lastMessage !== announcement) {
        liveRegion.dataset.lastMessage = announcement;
        liveRegion.textContent = announcement;
      }
    }
  },

  async openCartDrawer(trigger) {
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    if (!drawer) return;
    const returnTrigger = trigger?.closest('#AstrealMobileMenu')
      ? document.querySelector('.astreal-header [data-astreal-cart-open]') || document.querySelector(this.selectors.menuOpen)
      : trigger;
    this.cartStatus('');
    this.openOverlay(drawer, returnTrigger);
    const loading = drawer.querySelector('[data-astreal-cart-drawer-loading]');
    const error = drawer.querySelector('[data-astreal-cart-drawer-error]');
    const content = drawer.querySelector('[data-astreal-cart-drawer-content]');
    loading.hidden = false;
    error.hidden = true;
    content.hidden = true;
    try {
      const cart = await this.fetchCart();
      let presentation = null;
      try { presentation = await this.fetchCartPresentation(); } catch (error) { /* Static cart details remain usable. */ }
      this.updateCartIndicators(cart);
      this.renderCartDrawer(cart, presentation);
      loading.hidden = true;
      content.hidden = false;
    } catch (cartError) {
      loading.hidden = true;
      error.textContent = cartError.message || 'The cart could not be loaded.';
      error.hidden = false;
    }
  },

  createDrawerCartLine(item, currency, showCurrencyCode = false, presentation = null) {
    const line = document.createElement('article');
    line.className = 'astreal-cart-drawer-line';
    line.dataset.cartKey = item.key;
    line.dataset.cartVariantId = String(item.variant_id);
    line.dataset.cartQuantity = String(item.quantity);
    line.dataset.cartProductUrl = item.url || '';
    line.dataset.cartProductTitle = item.product_title || '';
    line.dataset.cartSellingPlanId = String(item.selling_plan_allocation?.selling_plan?.id || '');

    const media = document.createElement('a');
    media.className = 'astreal-cart-drawer-line__media';
    media.href = item.url;
    media.setAttribute('aria-label', `View ${item.product_title}`);
    const sourceLine = presentation
      ? [...presentation.querySelectorAll('[data-astreal-cart-line]')].find((candidate) => candidate.dataset.cartKey === item.key)
      : null;
    const cleanThumbnail = sourceLine?.querySelector('.astreal-cart-line__media img');
    if (cleanThumbnail) {
      media.append(cleanThumbnail.cloneNode(true));
    } else if (item.image) {
      const image = document.createElement('img');
      image.src = this.imageSource(item.image);
      image.alt = item.featured_image?.alt || '';
      image.width = 180;
      image.height = 180;
      image.loading = 'lazy';
      media.append(image);
    } else {
      const missing = document.createElement('span');
      missing.textContent = 'Image unavailable';
      media.append(missing);
    }
    line.append(media);

    const details = document.createElement('div');
    details.className = 'astreal-cart-drawer-line__details';
    const title = document.createElement('a');
    title.className = 'astreal-cart-drawer-line__title';
    title.href = item.url;
    title.textContent = item.product_title;
    details.append(title);

    const variantOptions = sourceLine?.querySelector('[data-astreal-cart-variant-options]');
    if (variantOptions) {
      details.append(variantOptions.cloneNode(true));
      sourceLine.querySelectorAll('[data-astreal-cart-option-metadata], [data-astreal-cart-variants], [data-astreal-cart-properties]').forEach((script) => details.append(script.cloneNode(true)));
    } else {
      const options = (item.options_with_values || []).filter((option) => String(option.value).toLowerCase() !== 'default title');
      if (options.length) {
        const optionText = document.createElement('p');
        optionText.className = 'astreal-cart-drawer-line__options';
        optionText.textContent = options.map((option) => {
          const name = String(option.name || '');
          const value = String(option.value || '');
          return /^(paper|finish)/i.test(name) && value.trim().toLowerCase() === 'matte'
            ? 'Matte finish'
            : `${name}: ${value}`;
        }).join(' · ');
        details.append(optionText);
      }
    }

    if (item.selling_plan_allocation?.selling_plan?.name) {
      const sellingPlan = document.createElement('p');
      sellingPlan.className = 'astreal-cart-line__property';
      sellingPlan.textContent = item.selling_plan_allocation.selling_plan.name;
      details.append(sellingPlan);
    }

    const price = document.createElement('p');
    price.className = 'astreal-cart-drawer-line__price';
    if (Number(item.original_line_price) > Number(item.final_line_price)) {
      const original = document.createElement('s');
      original.textContent = this.formatMoney(item.original_line_price, currency, showCurrencyCode);
      price.append(original, ' ');
    }
    const finalPrice = document.createElement('span');
    finalPrice.className = 'astreal-cart-line__line-total';
    finalPrice.textContent = `${this.formatMoney(item.final_line_price, currency, showCurrencyCode)}${Number(item.quantity) > 1 ? ' total' : ''}`;
    if (Number(item.quantity) > 1) {
      const unitPrice = document.createElement('span');
      unitPrice.className = 'astreal-cart-line__unit-price';
      unitPrice.textContent = `${this.formatMoney(item.final_price, currency, showCurrencyCode)} each`;
      price.append(unitPrice);
    }
    price.append(finalPrice);
    details.append(price);

    if (item.line_level_discount_allocations?.length) {
      const discounts = document.createElement('ul');
      discounts.className = 'astreal-cart-line__discounts';
      discounts.setAttribute('aria-label', 'Line discounts');
      item.line_level_discount_allocations.forEach((allocation) => {
        const discount = document.createElement('li');
        const title = allocation.discount_application?.title || 'Discount';
        discount.textContent = `${title} −${this.formatMoney(allocation.amount, currency, showCurrencyCode)}`;
        discounts.append(discount);
      });
      details.append(discounts);
    }

    const actions = document.createElement('div');
    actions.className = 'astreal-cart-drawer-line__actions';
    const quantity = document.createElement('div');
    quantity.className = 'astreal-quantity__control';
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.dataset.astrealCartMinus = '';
    minus.dataset.astrealCartControl = 'minus';
    minus.dataset.cartKey = item.key;
    minus.setAttribute('aria-label', `Decrease ${item.product_title} quantity`);
    minus.textContent = '−';
    const input = document.createElement('input');
    input.type = 'number';
    input.value = item.quantity;
    input.min = item.quantity_rule?.min || 1;
    input.step = item.quantity_rule?.increment || 1;
    if (item.quantity_rule?.max) input.max = item.quantity_rule.max;
    input.inputMode = 'numeric';
    input.dataset.astrealCartQuantity = '';
    input.dataset.astrealCartControl = 'quantity';
    input.dataset.cartKey = item.key;
    input.dataset.lastQuantity = String(item.quantity);
    input.setAttribute('aria-label', `Quantity for ${item.product_title}`);
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.dataset.astrealCartPlus = '';
    plus.dataset.astrealCartControl = 'plus';
    plus.dataset.cartKey = item.key;
    plus.setAttribute('aria-label', `Increase ${item.product_title} quantity`);
    plus.textContent = '+';
    quantity.append(minus, input, plus);
    actions.append(quantity);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'astreal-cart-drawer-line__remove';
    remove.dataset.astrealCartRemove = '';
    remove.dataset.astrealCartControl = 'remove';
    remove.dataset.cartKey = item.key;
    remove.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
    remove.textContent = 'Remove';
    actions.append(remove);

    details.append(actions);
    line.append(details);
    return line;
  },

  renderCartDrawer(cart, presentation = null) {
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    if (!drawer) return;
    const currency = cart.currency || drawer.dataset.currency || document.documentElement.dataset.astrealCurrency;
    const showItemCurrencyCode = drawer.dataset.itemCurrencyCodeEnabled === 'true';
    const showTotalCurrencyCode = drawer.dataset.totalCurrencyCodeEnabled === 'true';
    drawer.querySelector('[data-astreal-cart-drawer-count]').textContent = `(${cart.item_count})`;
    drawer.querySelector('[data-astreal-cart-drawer-total]').textContent = this.formatMoney(this.merchandiseSubtotal(cart), currency, showTotalCurrencyCode);
    const empty = drawer.querySelector('[data-astreal-cart-drawer-empty]');
    const items = drawer.querySelector('[data-astreal-cart-drawer-items]');
    const footer = drawer.querySelector('[data-astreal-cart-drawer-footer]');
    empty.hidden = cart.item_count > 0;
    footer.hidden = cart.item_count === 0;
    items.hidden = cart.item_count === 0;
    items.replaceChildren(...cart.items.map((item) => this.createDrawerCartLine(item, currency, showItemCurrencyCode, presentation)));
    this.initCartVariantOptions(items);
    this.updateFreeShippingProgress(cart, drawer);
  },

  cartFocusState() {
    const active = document.activeElement;
    const line = active?.closest?.('[data-astreal-cart-line], .astreal-cart-drawer-line');
    const root = line?.parentElement;
    return {
      key: line?.dataset.cartKey || active?.dataset?.cartKey || '',
      variantId: line?.dataset.cartVariantId || '',
      lineIndex: root ? [...root.querySelectorAll(':scope > [data-astreal-cart-line], :scope > .astreal-cart-drawer-line')].indexOf(line) : -1,
      control: active?.dataset?.astrealCartControl || '',
      optionIndex: active?.dataset?.optionIndex || '',
      optionValue: active?.dataset?.astrealCartOptionValue || '',
      label: active?.getAttribute?.('aria-label') || '',
      productTitle: line?.dataset.cartProductTitle || '',
      intent: '',
    };
  },

  findCartFocus(root, focusState) {
    if (!root || !focusState) return null;
    const lines = [...root.querySelectorAll('[data-astreal-cart-line], .astreal-cart-drawer-line')];
    if (!lines.length) return root.querySelector('[data-astreal-cart-empty-focus]');
    let line = lines.find((candidate) => candidate.dataset.cartKey === focusState.targetKey);
    if (!line && focusState.targetVariantId) line = lines.find((candidate) => candidate.dataset.cartVariantId === String(focusState.targetVariantId));
    if (!line && focusState.intent !== 'remove') line = lines.find((candidate) => candidate.dataset.cartKey === focusState.key);
    if (!line && focusState.intent !== 'remove') line = lines.find((candidate) => candidate.dataset.cartVariantId === String(focusState.variantId));
    if (!line && focusState.lineIndex >= 0) line = lines[Math.min(focusState.lineIndex, lines.length - 1)];
    if (!line) line = lines[0];
    if (focusState.control === 'option') {
      const options = [...line.querySelectorAll('[data-astreal-cart-option-value]')];
      const match = options.find((control) => control.dataset.optionIndex === String(focusState.optionIndex)
        && control.dataset.astrealCartOptionValue === focusState.optionValue);
      if (match) return match;
    }
    return [...line.querySelectorAll('[data-astreal-cart-control]')].find((control) => control.dataset.astrealCartControl === focusState.control)
      || [...line.querySelectorAll('button, input, a[href]')].find((element) => !element.disabled)
      || line.querySelector('a[href]');
  },

  restoreCartDrawerFocus(focusState = null) {
    const drawer = document.querySelector('[data-astreal-cart-drawer][aria-hidden="false"]');
    if (!drawer) return;
    const target = this.findCartFocus(drawer, focusState);
    (target || drawer.querySelector('[data-astreal-cart-empty-focus], [data-astreal-overlay-close], button, input, a'))?.focus({ preventScroll: true });
  },

  cartStatus(message, { transient = false } = {}) {
    window.clearTimeout(this.cartStatusTimer);
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    const owner = drawer?.getAttribute('aria-hidden') === 'false'
      ? drawer.querySelector('[data-astreal-cart-drawer-status]')
      : document.querySelector('[data-astreal-cart-status]');
    document.querySelectorAll('[data-astreal-cart-status], [data-astreal-cart-drawer-status]').forEach((status) => {
      status.textContent = status === owner ? message : '';
    });
    if (message && transient) this.cartStatusTimer = window.setTimeout(() => this.cartStatus(''), 8000);
  },

  setCartBusy(busy) {
    this.cartBusy = busy;
    document.querySelectorAll('[data-astreal-cart-minus], [data-astreal-cart-plus], [data-astreal-cart-quantity], [data-astreal-cart-remove], [data-astreal-cart-option-editor-open], [data-astreal-cart-editor-option-value], [data-astreal-cart-option-editor-apply], [data-astreal-cart-checkout]').forEach((control) => {
      if (busy) {
        if (!control.hasAttribute('data-astreal-was-disabled')) control.dataset.astrealWasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.hasAttribute('data-astreal-was-disabled')) {
        control.disabled = control.dataset.astrealWasDisabled === 'true';
        delete control.dataset.astrealWasDisabled;
      }
    });
    document.querySelectorAll('[data-astreal-cart-page], [data-astreal-cart-drawer]').forEach((root) => {
      if (busy) root.setAttribute('aria-busy', 'true');
      else root.removeAttribute('aria-busy');
    });
  },

  validatedCartQuantity(input) {
    const value = Number(input.value);
    const minimum = Number(input.min || 1);
    const maximum = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step || 1);
    const validStep = Number.isFinite(step) && step > 0 && Math.abs(((value - minimum) / step) - Math.round((value - minimum) / step)) < 0.0001;
    if (!Number.isInteger(value) || value < minimum || value > maximum || !validStep) {
      input.value = input.dataset.lastQuantity || String(minimum);
      this.cartStatus('Enter a valid whole-number quantity.');
      return null;
    }
    return value;
  },

  cartSellingPlanId(item) {
    return String(item?.selling_plan_allocation?.selling_plan?.id || item?.selling_plan_allocation?.selling_plan_id || '');
  },

  cartPropertiesSignature(properties = {}) {
    return JSON.stringify(Object.entries(properties || {}).sort(([left], [right]) => left.localeCompare(right)));
  },

  cartLineSignature(item, variantId = item?.variant_id) {
    return [String(variantId || ''), this.cartSellingPlanId(item), this.cartPropertiesSignature(item?.properties)].join('|');
  },

  cartLinesWithSignature(cart, signature) {
    return (cart?.items || []).filter((item) => this.cartLineSignature(item) === signature);
  },

  cartVariantAllowed(variant, line) {
    if (!variant?.available) return false;
    const quantity = Number(line.dataset.cartQuantity || 1);
    const rule = variant.quantityRule || {};
    const minimum = Number(rule.min || 1);
    const maximum = Number(rule.max || 0);
    const increment = Number(rule.increment || 1);
    if (quantity < minimum || (maximum > 0 && quantity > maximum)) return false;
    if (increment > 0 && Math.abs(((quantity - minimum) / increment) - Math.round((quantity - minimum) / increment)) > 0.0001) return false;
    const sellingPlanId = String(line.dataset.cartSellingPlanId || '');
    if (sellingPlanId) return (variant.sellingPlanIds || []).map(String).includes(sellingPlanId);
    return !variant.requiresSellingPlan;
  },

  initCartVariantOptions(scope = document) {
    scope.querySelectorAll('[data-astreal-cart-line], .astreal-cart-drawer-line').forEach((line) => {
      line._astrealCartVariants = this.parseJsonScript(line.querySelector('[data-astreal-cart-variants]'), []);
      line._astrealCartProperties = this.parseJsonScript(line.querySelector('[data-astreal-cart-properties]'), {});
      line._astrealCartOptionMetadata = this.parseJsonScript(line.querySelector('[data-astreal-cart-option-metadata]'), []);
      const current = line._astrealCartVariants.find((variant) => String(variant.id) === String(line.dataset.cartVariantId));
      line._astrealCartSelection = current ? [...current.options] : [];
    });
  },

  openCartOptionEditor(trigger) {
    if (this.cartBusy || trigger.disabled) return;
    const line = trigger.closest('[data-astreal-cart-line], .astreal-cart-drawer-line');
    const overlay = document.querySelector('[data-astreal-cart-option-editor]');
    const metadata = (line?._astrealCartOptionMetadata || []).filter((option) => Array.isArray(option.values) && option.values.length);
    if (!line?._astrealCartVariants?.length || !line._astrealCartSelection?.length || !metadata.length || !overlay) {
      this.cartStatus('Options for this item could not be loaded. Refresh and try again.');
      return;
    }

    this.cartOptionEditor = {
      line,
      trigger,
      metadata,
      selection: [...line._astrealCartSelection],
    };
    const product = overlay.querySelector('[data-astreal-cart-option-editor-product]');
    if (product) product.textContent = line.dataset.cartProductTitle || 'Cart item';
    const status = overlay.querySelector('[data-astreal-cart-option-editor-status]');
    if (status) status.textContent = '';
    this.renderCartOptionEditor();
    trigger.setAttribute('aria-expanded', 'true');
    this.openOverlay(overlay, trigger);
  },

  renderCartOptionEditor() {
    const state = this.cartOptionEditor;
    const overlay = document.querySelector('[data-astreal-cart-option-editor]');
    const mount = overlay?.querySelector('[data-astreal-cart-option-editor-options]');
    if (!state || !mount) return;
    mount.replaceChildren();
    let hasAutofocus = false;
    let hasMatteNote = false;

    state.metadata.forEach((option, metadataIndex) => {
      const optionIndex = Math.max(0, Number(option.position || metadataIndex + 1) - 1);
      const values = (option.values || []).map((entry) => typeof entry === 'string' ? { name: entry } : entry).filter((entry) => entry?.name);
      if (!values.length) return;
      if (this.isRedundantMatteOption(option, values)) {
        if (!hasMatteNote) {
          const note = document.createElement('p');
          note.className = 'astreal-product-option-note';
          note.textContent = 'Matte finish';
          mount.append(note);
          hasMatteNote = true;
        }
        return;
      }
      const isColour = Boolean(option.isColour || values.some((entry) => entry.hasNativeSwatch));
      const fieldset = document.createElement('fieldset');
      fieldset.className = `astreal-product-option astreal-cart-option-editor__option${isColour ? ' astreal-product-option--colour' : ''}`;
      fieldset.dataset.astrealCartEditorOption = '';
      fieldset.dataset.optionIndex = String(optionIndex);
      const legend = document.createElement('legend');
      const legendLabel = document.createElement('span');
      legendLabel.textContent = option.name;
      if (isColour) {
        legendLabel.append(': ');
        const selectedValue = document.createElement('span');
        selectedValue.dataset.astrealCartEditorOptionSelection = '';
        selectedValue.textContent = state.selection[optionIndex] || '';
        legendLabel.append(selectedValue);
      }
      legend.append(legendLabel);
      fieldset.append(legend);

      const valueGroup = document.createElement('div');
      valueGroup.className = 'astreal-product-option__values';
      values.forEach((valueMetadata) => {
        const value = valueMetadata.name;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.astrealCartEditorOptionValue = value;
        button.dataset.optionIndex = String(optionIndex);
        button.setAttribute('aria-pressed', String(state.selection[optionIndex] === value));
        if (!hasAutofocus && state.selection[optionIndex] === value) {
          button.setAttribute('autofocus', '');
          hasAutofocus = true;
        }
        if (isColour) {
          button.className = 'astreal-colour-swatch';
          button.setAttribute('aria-label', `${option.name}: ${value}`);
          button.title = value;
          const visual = document.createElement('span');
          visual.className = 'astreal-colour-swatch__visual';
          visual.setAttribute('aria-hidden', 'true');
          const hiddenValue = document.createElement('span');
          hiddenValue.className = 'astreal-visually-hidden';
          hiddenValue.textContent = value;
          button.append(visual, hiddenValue);
          this.applyColourSwatch(button, valueMetadata, value);
        } else {
          button.textContent = value;
          button.setAttribute('aria-label', `${option.name}: ${value}`);
        }
        valueGroup.append(button);
      });
      fieldset.append(valueGroup);
      mount.append(fieldset);
    });
    this.refreshCartOptionEditor();
  },

  refreshCartOptionEditor() {
    const state = this.cartOptionEditor;
    const overlay = document.querySelector('[data-astreal-cart-option-editor]');
    if (!state || !overlay) return;
    const optionIndexes = state.metadata.map((option, index) => Math.max(0, Number(option.position || index + 1) - 1));
    const allowedVariants = state.line._astrealCartVariants.filter((variant) => this.cartVariantAllowed(variant, state.line));
    overlay.querySelectorAll('[data-astreal-cart-editor-option]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex);
      const editorPosition = optionIndexes.indexOf(optionIndex);
      fieldset.querySelectorAll('[data-astreal-cart-editor-option-value]').forEach((button) => {
        const value = button.dataset.astrealCartEditorOptionValue;
        const available = allowedVariants.some((variant) => {
          const options = this.variantOptions(variant);
          if (options[optionIndex] !== value) return false;
          return optionIndexes.slice(0, editorPosition).every((index) => options[index] === state.selection[index]);
        });
        button.disabled = !available;
        button.setAttribute('aria-pressed', String(state.selection[optionIndex] === value));
      });
      const selection = fieldset.querySelector('[data-astreal-cart-editor-option-selection]');
      if (selection) selection.textContent = state.selection[optionIndex] || '';
    });

    const targetVariant = this.variantForSelection(state.line._astrealCartVariants, state.selection);
    const targetAvailable = Boolean(targetVariant && this.cartVariantAllowed(targetVariant, state.line));
    const changed = targetAvailable && String(targetVariant.id) !== String(state.line.dataset.cartVariantId);
    const apply = overlay.querySelector('[data-astreal-cart-option-editor-apply]');
    if (apply) apply.disabled = !changed || this.cartBusy;
    const status = overlay.querySelector('[data-astreal-cart-option-editor-status]');
    if (status) status.textContent = targetVariant && !targetAvailable ? 'This option combination is unavailable.' : '';
    const price = overlay.querySelector('[data-astreal-cart-option-editor-price]');
    if (price) {
      price.replaceChildren();
      if (targetVariant) {
        const currency = overlay.dataset.currency || document.documentElement.dataset.astrealCurrency;
        const showCurrencyCode = overlay.dataset.currencyCodeEnabled === 'true';
        const current = document.createElement('span');
        current.textContent = this.formatMoney(targetVariant.price, currency, showCurrencyCode);
        price.append(current);
        if (Number(targetVariant.compareAtPrice) > Number(targetVariant.price)) {
          const compare = document.createElement('s');
          compare.textContent = this.formatMoney(targetVariant.compareAtPrice, currency, showCurrencyCode);
          price.append(' ', compare);
        }
      }
    }
  },

  selectCartEditorOption(button) {
    const state = this.cartOptionEditor;
    if (!state || button.disabled || this.cartBusy) return;
    const optionIndex = Number(button.dataset.optionIndex);
    const value = button.dataset.astrealCartEditorOptionValue;
    const previousSelection = [...state.selection];
    const optionIndexes = state.metadata.map((option, index) => Math.max(0, Number(option.position || index + 1) - 1));
    const editorPosition = optionIndexes.indexOf(optionIndex);
    const candidates = state.line._astrealCartVariants.filter((variant) => {
      if (!this.cartVariantAllowed(variant, state.line)) return false;
      const options = this.variantOptions(variant);
      if (options[optionIndex] !== value) return false;
      return optionIndexes.slice(0, editorPosition).every((index) => options[index] === state.selection[index]);
    });
    candidates.sort((left, right) => {
      const leftOptions = this.variantOptions(left);
      const rightOptions = this.variantOptions(right);
      const leftScore = leftOptions.reduce((score, option, index) => score + Number(option === previousSelection[index]), 0);
      const rightScore = rightOptions.reduce((score, option, index) => score + Number(option === previousSelection[index]), 0);
      return rightScore - leftScore;
    });
    if (!candidates.length) return;
    state.selection = this.variantOptions(candidates[0]);
    this.refreshCartOptionEditor();
  },

  applyCartOptionEditor() {
    const state = this.cartOptionEditor;
    const overlay = document.querySelector('[data-astreal-cart-option-editor]');
    if (!state || !overlay || this.cartBusy) return;
    if (!state.line.isConnected || !state.trigger.isConnected) {
      const status = overlay.querySelector('[data-astreal-cart-option-editor-status]');
      if (status) status.textContent = 'The cart changed. Close this editor and try again.';
      return;
    }
    const targetVariant = this.variantForSelection(state.line._astrealCartVariants, state.selection);
    if (!targetVariant || !this.cartVariantAllowed(targetVariant, state.line)) {
      const status = overlay.querySelector('[data-astreal-cart-option-editor-status]');
      if (status) status.textContent = 'This option combination is unavailable.';
      return;
    }
    if (String(targetVariant.id) === String(state.line.dataset.cartVariantId)) {
      this.closeOverlay(overlay);
      return;
    }
    const { line, trigger } = state;
    this.closeOverlay(overlay);
    this.replaceCartLineVariant(line, targetVariant, trigger);
  },

  async postCartJson(endpoint, body) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    let result = {};
    try { result = await response.json(); } catch (error) { /* Reconciliation handles an empty response. */ }
    if (!response.ok) throw new Error(result.description || result.message || 'The cart could not be updated.');
    return result;
  },

  async presentCart(cart, focusState = null) {
    this.updateCartIndicators(cart);
    let presentation = null;
    try { presentation = await this.fetchCartPresentation(); } catch (error) { /* Keep commerce state authoritative even if markup refresh fails. */ }
    const currentPage = document.querySelector('[data-astreal-cart-page]');
    if (currentPage && presentation) {
      currentPage.replaceWith(presentation);
      this.initCartVariantOptions(presentation);
      const pageFocus = this.findCartFocus(presentation, focusState);
      pageFocus?.focus({ preventScroll: true });
    }
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    if (drawer?.getAttribute('aria-hidden') === 'false') {
      this.renderCartDrawer(cart, presentation);
      this.restoreCartDrawerFocus(focusState);
    }
    this.updateFreeShippingProgress(cart, document, true);
    return Boolean(!currentPage || presentation);
  },

  async rollbackCartVariantAddition(snapshot, currentCart, targetSignature, sourceQuantity, endpoint) {
    const previousQuantity = this.cartLinesWithSignature(snapshot, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
    const snapshotKeys = new Set(this.cartLinesWithSignature(snapshot, targetSignature).map((item) => item.key));
    let restored = currentCart;
    let currentQuantity = this.cartLinesWithSignature(restored, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
    if (currentQuantity <= previousQuantity) return restored;
    if (currentQuantity - previousQuantity < sourceQuantity) throw new Error('The cart needs review because the replacement line could not be identified safely.');

    for (let attempt = 0; currentQuantity > previousQuantity && attempt < 50; attempt += 1) {
      const currentLines = this.cartLinesWithSignature(restored, targetSignature);
      const targetLine = currentLines.find((item) => !snapshotKeys.has(item.key)) || currentLines[currentLines.length - 1];
      if (!targetLine) break;
      const excess = currentQuantity - previousQuantity;
      const restoredQuantity = Math.max(0, Number(targetLine.quantity || 0) - Math.min(Number(targetLine.quantity || 0), excess));
      const result = await this.postCartJson(endpoint, { id: targetLine.key, quantity: restoredQuantity });
      restored = Array.isArray(result.items) ? result : await this.fetchCart();
      const nextQuantity = this.cartLinesWithSignature(restored, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
      if (nextQuantity >= currentQuantity) throw new Error('The cart needs review because the replacement could not be rolled back safely.');
      currentQuantity = nextQuantity;
    }

    const restoredTargetQuantity = this.cartLinesWithSignature(restored, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
    if (restoredTargetQuantity !== previousQuantity) throw new Error('The cart needs review because the replacement could not be fully rolled back.');
    return restored;
  },

  async replaceCartLineVariant(line, targetVariant, trigger) {
    if (this.cartBusy) return;
    const key = line.dataset.cartKey;
    const focusState = this.cartFocusState();
    focusState.control = trigger.dataset.astrealCartControl || 'option-editor';
    focusState.optionIndex = trigger.dataset.optionIndex || '';
    focusState.optionValue = trigger.dataset.astrealCartOptionValue || '';
    focusState.targetVariantId = String(targetVariant.id);
    focusState.intent = 'variant';
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    const page = document.querySelector('[data-astreal-cart-page]');
    const addEndpoint = drawer?.dataset.cartAddUrl || page?.dataset.cartAddUrl || '/cart/add.js';
    const changeEndpoint = drawer?.dataset.cartChangeUrl || page?.dataset.cartChangeUrl || '/cart/change.js';
    this.setCartBusy(true);
    this.cartStatus('Updating item options…');
    let authoritativeCart = null;
    let failureMessage = '';
    try {
      const snapshot = await this.fetchCart();
      const source = snapshot.items.find((item) => item.key === key);
      if (!source) throw new Error('This cart line changed. Please try again.');
      const sourceQuantity = Number(source.quantity || 0);
      const targetSignature = this.cartLineSignature(source, targetVariant.id);
      const previousTargetQuantity = this.cartLinesWithSignature(snapshot, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
      const itemPayload = {
        id: Number(targetVariant.id),
        quantity: sourceQuantity,
        properties: source.properties || {},
      };
      const sellingPlanId = this.cartSellingPlanId(source);
      if (sellingPlanId) itemPayload.selling_plan = Number(sellingPlanId);

      let afterAdd;
      try {
        await this.postCartJson(addEndpoint, { items: [itemPayload] });
        afterAdd = await this.fetchCart();
      } catch (addError) {
        afterAdd = await this.fetchCart();
        const targetQuantity = this.cartLinesWithSignature(afterAdd, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
        if (targetQuantity - previousTargetQuantity < sourceQuantity) throw addError;
      }

      const addedTargetQuantity = this.cartLinesWithSignature(afterAdd, targetSignature).reduce((total, item) => total + Number(item.quantity || 0), 0);
      if (addedTargetQuantity - previousTargetQuantity < sourceQuantity) throw new Error('The replacement variant was not added. Your original item is unchanged.');
      const sourceSignature = this.cartLineSignature(source);
      let currentSource = afterAdd.items.find((item) => item.key === source.key);
      if (!currentSource) {
        const sourceMatches = this.cartLinesWithSignature(afterAdd, sourceSignature);
        if (sourceMatches.length === 1) currentSource = sourceMatches[0];
      }
      if (!currentSource) {
        authoritativeCart = await this.rollbackCartVariantAddition(snapshot, afterAdd, targetSignature, sourceQuantity, changeEndpoint);
        throw new Error('The item changed while it was updating. The original cart was restored.');
      }

      try {
        const removalResult = await this.postCartJson(changeEndpoint, { id: currentSource.key, quantity: 0 });
        authoritativeCart = Array.isArray(removalResult.items) ? removalResult : await this.fetchCart();
      } catch (removeError) {
        const reconciled = await this.fetchCart();
        const sourceRemains = reconciled.items.some((item) => item.key === currentSource.key)
          || this.cartLinesWithSignature(reconciled, sourceSignature).length > 0;
        if (!sourceRemains) {
          authoritativeCart = reconciled;
        } else {
          authoritativeCart = await this.rollbackCartVariantAddition(snapshot, reconciled, targetSignature, sourceQuantity, changeEndpoint);
          failureMessage = 'The option change could not be completed. Your original item was kept.';
          focusState.intent = 'variant-error';
          focusState.targetKey = '';
          focusState.targetVariantId = '';
        }
      }

      const finalTargets = this.cartLinesWithSignature(authoritativeCart, targetSignature);
      if (!failureMessage && finalTargets.length) focusState.targetKey = finalTargets[0].key;
    } catch (error) {
      failureMessage = error.message || 'The item options could not be updated.';
      focusState.intent = 'variant-error';
      focusState.targetKey = '';
      focusState.targetVariantId = '';
      try { authoritativeCart = authoritativeCart || await this.fetchCart(); } catch (refreshError) { /* Preserve the rendered cart if reconciliation fails. */ }
    }

    let reloadCartPage = false;
    try {
      if (authoritativeCart) {
        const presented = await this.presentCart(authoritativeCart, focusState);
        if (!presented) throw new Error('The cart display could not be refreshed.');
      }
      this.cartStatus(failureMessage || `${focusState.productTitle || 'Item'} options updated.`, { transient: !failureMessage });
    } catch (presentationError) {
      reloadCartPage = Boolean(document.querySelector('[data-astreal-cart-page]'));
      this.cartStatus(reloadCartPage
        ? 'The cart changed, but its display could not be refreshed. Refreshing the cart…'
        : 'The cart changed, but its display could not be refreshed. Close and reopen the cart to retry.');
    } finally {
      this.setCartBusy(false);
      if (reloadCartPage) window.location.reload();
    }
  },

  async changeCartLine(key, quantity) {
    const nextQuantity = Number(quantity);
    if (!key || !Number.isInteger(nextQuantity) || nextQuantity < 0 || this.cartBusy) return;
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    const page = document.querySelector('[data-astreal-cart-page]');
    const endpoint = drawer?.dataset.cartChangeUrl || page?.dataset.cartChangeUrl || '/cart/change.js';
    const focusState = this.cartFocusState();
    focusState.intent = nextQuantity === 0 ? 'remove' : 'quantity';
    this.setCartBusy(true);
    this.cartStatus(nextQuantity === 0 ? 'Removing item…' : 'Updating cart…');
    let authoritativeCart = null;
    let failureMessage = '';
    try {
      const snapshot = await this.fetchCart();
      const source = snapshot.items.find((item) => item.key === key);
      if (!source) throw new Error('This cart line changed. Please try again.');
      try {
        const result = await this.postCartJson(endpoint, { id: key, quantity: nextQuantity });
        authoritativeCart = Array.isArray(result.items) ? result : await this.fetchCart();
      } catch (requestError) {
        authoritativeCart = await this.fetchCart();
        const sourceSignature = this.cartLineSignature(source);
        const currentSource = authoritativeCart.items.find((item) => item.key === key)
          || this.cartLinesWithSignature(authoritativeCart, sourceSignature)[0];
        const committed = nextQuantity === 0 ? !currentSource : Number(currentSource?.quantity) === nextQuantity;
        if (!committed) throw requestError;
      }
    } catch (error) {
      failureMessage = error.message || 'The cart could not be updated.';
      focusState.intent = 'quantity-error';
      focusState.targetKey = '';
      focusState.targetVariantId = '';
      try { authoritativeCart = authoritativeCart || await this.fetchCart(); } catch (refreshError) { /* Keep the previous rendered state. */ }
    }
    let reloadCartPage = false;
    try {
      if (authoritativeCart) {
        const presented = await this.presentCart(authoritativeCart, focusState);
        if (!presented) throw new Error('The cart display could not be refreshed.');
      }
      const successMessage = nextQuantity === 0
        ? `${focusState.productTitle || 'Item'} removed from cart.`
        : 'Cart updated.';
      this.cartStatus(failureMessage || successMessage, { transient: !failureMessage });
    } catch (presentationError) {
      reloadCartPage = Boolean(document.querySelector('[data-astreal-cart-page]'));
      this.cartStatus(reloadCartPage
        ? 'The cart changed, but its display could not be refreshed. Refreshing the cart…'
        : 'The cart changed, but its display could not be refreshed. Close and reopen the cart to retry.');
    } finally {
      this.setCartBusy(false);
      if (reloadCartPage) window.location.reload();
    }
  },

  stepCartQuantity(button, direction) {
    if (this.cartBusy) return;
    const control = button.closest('.astreal-quantity__control');
    const input = control?.querySelector('[data-astreal-cart-quantity]');
    if (!input) return;
    const step = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value || min);
    const next = direction < 0 && current <= min
      ? 0
      : Math.min(max, Math.max(min, current + (direction * step)));
    if (next === Number(input.value)) return;
    this.changeCartLine(input.dataset.cartKey, next);
  },

  contactFieldKey(field) {
    const name = field?.name || '';
    if (name.includes('[name]')) return 'name';
    if (name.includes('[email]')) return 'email';
    if (name.includes('[Topic]')) return 'topic';
    if (name.includes('[body]')) return 'body';
    return '';
  },

  setContactFieldError(form, field, message = '') {
    const key = this.contactFieldKey(field);
    if (!key) return;
    const error = form.querySelector(`[data-contact-error-for="${key}"]`);
    if (error) error.textContent = message;
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
  },

  validateContactForm(form) {
    let firstInvalid = null;
    form.querySelectorAll('[required]').forEach((field) => {
      let message = '';
      if (!field.value.trim()) message = 'This field is required.';
      else if (field.type === 'email' && !field.validity.valid) message = 'Enter a valid email address.';
      this.setContactFieldError(form, field, message);
      if (message && !firstInvalid) firstInvalid = field;
    });
    firstInvalid?.focus();
    return !firstInvalid;
  },

  saveContactCustomFields(form) {
    const orderNumber = form.elements.namedItem('contact[Order number]')?.value || '';
    const topic = form.elements.namedItem('contact[Topic]')?.value || '';
    try {
      window.sessionStorage.setItem('astreal-contact-custom-fields', JSON.stringify({ orderNumber, topic }));
    } catch (error) {
      // Shopify still receives both values when session storage is unavailable.
    }
  },

  initContactForm() {
    const form = document.querySelector('[data-astreal-contact-form]');
    if (!form) return;
    form.noValidate = true;
    const success = form.querySelector('[data-astreal-contact-success]');
    if (success) {
      try { window.sessionStorage.removeItem('astreal-contact-custom-fields'); } catch (error) { /* No action needed. */ }
      success.focus();
      return;
    }
    if (!form.querySelector('.astreal-contact-form__server-errors')) return;
    try {
      const stored = JSON.parse(window.sessionStorage.getItem('astreal-contact-custom-fields') || '{}');
      const orderNumber = form.elements.namedItem('contact[Order number]');
      const topic = form.elements.namedItem('contact[Topic]');
      if (orderNumber && stored.orderNumber) orderNumber.value = stored.orderNumber;
      if (topic && stored.topic) topic.value = stored.topic;
    } catch (error) {
      // Keep Shopify's server-rendered field values when storage is unavailable.
    }
  },

  setFaqItem(button, expanded) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    button.setAttribute('aria-expanded', String(expanded));
    if (panel) panel.hidden = !expanded;
    const mark = button.querySelector('[data-astreal-faq-mark]');
    if (mark) mark.textContent = expanded ? '−' : '+';
  },

  initFaq(scope = document) {
    scope.querySelectorAll('[data-astreal-faq-list]').forEach((faq) => {
      if (faq.dataset.astrealFaqEnhanced === 'true') return;
      faq.dataset.astrealFaqEnhanced = 'true';
      faq.querySelectorAll('[data-astreal-faq-button]').forEach((button) => {
        const item = button.closest('[data-astreal-faq-item]');
        this.setFaqItem(button, item?.hasAttribute('data-astreal-faq-initially-open'));
      });
    });
  },

  toggleFaq(button) {
    const faq = button.closest('[data-astreal-faq-list]');
    if (!faq) return;
    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';
    faq.querySelectorAll('[data-astreal-faq-button]').forEach((candidate) => {
      this.setFaqItem(candidate, candidate === button && shouldOpen);
    });
  },

  async refreshCartCount() {
    try {
      const cart = await this.fetchCart();
      this.updateCartIndicators(cart);
      this.updateFreeShippingProgress(cart, document, true);
    } catch (error) {
      // The add operation already succeeded; the next navigation refreshes count.
    }
  },

  async refreshCartSurfacesFromServer() {
    this.cartStatus('');
    const activeCartSurface = document.activeElement?.closest?.('[data-astreal-cart-page], [data-astreal-cart-drawer]');
    const focusState = activeCartSurface ? this.cartFocusState() : null;
    const cart = await this.fetchCart();
    const cartPage = document.querySelector('[data-astreal-cart-page]');
    const drawer = document.querySelector('[data-astreal-cart-drawer]');
    if (cartPage || drawer?.getAttribute('aria-hidden') === 'false') {
      const presented = await this.presentCart(cart, focusState);
      if (!presented) {
        const reloadCartPage = Boolean(cartPage);
        this.cartStatus(reloadCartPage
          ? 'The cart changed, but its display could not be refreshed. Refreshing the cart…'
          : 'The cart changed, but its display could not be refreshed. Close and reopen the cart to retry.');
        if (reloadCartPage) window.location.reload();
      }
      return;
    }
    this.updateCartIndicators(cart);
    this.updateFreeShippingProgress(cart, document, true);
  },

  queueCartSurfaceRefresh(event) {
    const mutation = event?.promise
      ? Promise.resolve(event.promise).then(
        () => ({ status: 'fulfilled' }),
        (error) => ({ status: 'rejected', error }),
      )
      : Promise.resolve({ status: 'fulfilled' });
    this.cartSurfaceRefreshQueue = this.cartSurfaceRefreshQueue
      .catch(() => {})
      .then(() => mutation)
      .then((result) => {
        if (result.status === 'rejected') throw result.error;
        return this.refreshCartSurfacesFromServer();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ASTREAL cart] Could not refresh after an external cart update.', error);
      });
  },

  productRailGeometry(viewport) {
    const cards = viewport.querySelectorAll('.astreal-product-card');
    const track = viewport.querySelector('.astreal-product-rail__track');
    const gap = track ? Number.parseFloat(window.getComputedStyle(track).columnGap) || 0 : 0;
    const step = (cards[0]?.getBoundingClientRect().width || viewport.clientWidth) + gap;
    const visible = Math.max(1, Math.round((viewport.clientWidth + gap) / step));
    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const pages = Array.from({ length: Math.max(1, Math.ceil(cards.length / visible)) }, (_, index) => Math.min(max, index * visible * step));
    return { step, visible, max, pages };
  },

  updateProductRail(viewport) {
    if (!viewport) return;
    const rail = viewport.closest('[data-astreal-rail]');
    const controls = rail?.querySelector('[data-astreal-rail-controls]');
    if (!controls) return;
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const overflows = maxScroll > 2;
    controls.hidden = !overflows;
    const previous = controls.querySelector('[data-astreal-rail-previous]');
    const next = controls.querySelector('[data-astreal-rail-next]');
    // Keep the activated arrow focusable when it reaches an endpoint.
    if (previous) previous.setAttribute('aria-disabled', String(!overflows || viewport.scrollLeft <= 2));
    if (next) next.setAttribute('aria-disabled', String(!overflows || viewport.scrollLeft >= maxScroll - 2));
  },

  initProductRails(scope = document) {
    const viewports = [
      ...(scope.matches?.('[data-astreal-product-rail]') ? [scope] : []),
      ...scope.querySelectorAll('[data-astreal-product-rail]'),
    ];
    viewports.forEach((viewport) => {
      if (viewport.dataset.astrealRailEnhanced !== 'true') {
        viewport.dataset.astrealRailEnhanced = 'true';
        viewport.addEventListener('scroll', () => this.updateProductRail(viewport), { passive: true });
        if ('ResizeObserver' in window) {
          viewport._astrealRailObserver = new ResizeObserver(() => {
            const geometry = this.productRailGeometry(viewport);
            const old = viewport._astrealRailGeometry;
            if (old && (old.visible !== geometry.visible || Math.abs(old.step - geometry.step) > 1)) {
              viewport._astrealCancelRailMotion?.();
              const firstIndex = Math.round(viewport.scrollLeft / old.step);
              const page = Math.floor(firstIndex / geometry.visible);
              viewport.scrollTo({ left: geometry.pages[Math.min(page, geometry.pages.length - 1)], behavior: 'instant' });
            }
            viewport._astrealRailGeometry = geometry;
            this.updateProductRail(viewport);
          });
          viewport._astrealRailObserver.observe(viewport);
          const track = viewport.querySelector('.astreal-product-rail__track');
          if (track) viewport._astrealRailObserver.observe(track);
        }
      }
      window.requestAnimationFrame(() => this.updateProductRail(viewport));
    });
  },

  scrollProductRail(button, direction) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const viewportId = button.getAttribute('aria-controls');
    const viewport = viewportId ? document.getElementById(viewportId) : null;
    if (!viewport) return;
    const { pages, max } = this.productRailGeometry(viewport);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    viewport._astrealCancelRailMotion?.();
    const start = viewport.scrollLeft;
    const target = direction > 0
      ? pages.find((position) => position > start + 2) ?? max
      : [...pages].reverse().find((position) => position < start - 2) ?? 0;
    if (reducedMotion) {
      viewport.scrollTo({ left: target, behavior: 'instant' });
      this.updateProductRail(viewport);
      return;
    }
    const snap = viewport.style.scrollSnapType;
    const behavior = viewport.style.scrollBehavior;
    viewport.style.scrollSnapType = 'none';
    viewport.style.scrollBehavior = 'auto';
    let frame;
    const cancel = () => {
      window.cancelAnimationFrame(frame);
      viewport.style.scrollSnapType = snap;
      viewport.style.scrollBehavior = behavior;
      viewport._astrealCancelRailMotion = null;
    };
    viewport._astrealCancelRailMotion = cancel;
    const started = performance.now();
    const move = (now) => {
      const elapsed = Math.min(1, (now - started) / 220);
      viewport.scrollLeft = start + (target - start) * (1 - (1 - elapsed) ** 3);
      if (elapsed < 1) frame = window.requestAnimationFrame(move);
      else {
        cancel();
        this.updateProductRail(viewport);
      }
    };
    frame = window.requestAnimationFrame(move);
  },

  bind() {
    // Restore the announcement preference after back/forward navigation or a change in another tab.
    window.addEventListener('pageshow', () => this.initTickerMotion());
    window.addEventListener('storage', (event) => {
      if (event.key === 'astreal-ticker-motion' || event.key === null) {
        this.tickerMotionPreference = null;
        this.initTickerMotion();
      }
    });
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => this.initTickerMotion());
    window.visualViewport?.addEventListener('resize', () => this.syncDialogViewport());
    window.addEventListener('resize', () => this.syncDialogViewport());
    document.addEventListener('pointerdown', (event) => {
      const rail = event.target.closest?.('[data-astreal-product-rail]');
      rail?._astrealCancelRailMotion?.();
      this.railPointer = rail && event.isPrimary ? { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false } : null;
    }, { passive: true });
    document.addEventListener('pointermove', (event) => {
      if (this.railPointer?.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - this.railPointer.x, event.clientY - this.railPointer.y) > 10) this.railPointer.moved = true;
    }, { passive: true });
    const endRailPointer = (event) => {
      if (this.railPointer?.id !== event.pointerId) return;
      if (this.railPointer.moved) this.railClickSuppressedUntil = Date.now() + 350;
      this.railPointer = null;
    };
    document.addEventListener('pointerup', endRailPointer, { passive: true });
    document.addEventListener('pointercancel', endRailPointer, { passive: true });
    const desktopMenuQuery = window.matchMedia('(min-width: 900px)');
    desktopMenuQuery.addEventListener('change', (event) => {
      if (event.matches) this.setMenu(false);
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.queueCartSurfaceRefresh(event));
    document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => this.queueCartSurfaceRefresh(event));

    document.addEventListener('click', (event) => {
      const mediaView = event.target.closest('[data-astreal-media-view]');
      if (mediaView) {
        const media = mediaView.closest('[data-astreal-buying-media]');
        media._astrealView = mediaView.dataset.astrealMediaView;
        const quick = media.closest('[data-astreal-quick-add-dialog]');
        if (quick) {
          const variant = this.variantForSelection(this.quickProduct.variants, this.quickSelection);
          this.updateBuyingMedia(media, this.quickPosterMedia, this.quickProduct, variant);
        } else {
          const root = media.closest('[data-astreal-product]');
          this.updateProductVariant(root);
        }
        return;
      }
      const tickerMotionToggle = event.target.closest(this.selectors.tickerMotionToggle);
      if (tickerMotionToggle) {
        const track = document.getElementById(tickerMotionToggle.getAttribute('aria-controls'));
        const ticker = track?.closest(this.selectors.ticker);
        this.setTickerMotion(ticker?.dataset.astrealTickerMotion === 'paused' ? 'playing' : 'paused', true);
        return;
      }
      const railPrevious = event.target.closest('[data-astreal-rail-previous]');
      if (railPrevious) {
        this.scrollProductRail(railPrevious, -1);
        return;
      }
      const railNext = event.target.closest('[data-astreal-rail-next]');
      if (railNext) {
        this.scrollProductRail(railNext, 1);
        return;
      }
      const toggle = event.target.closest(this.selectors.themeToggle);
      if (toggle) {
        this.setTheme(document.documentElement.dataset.astrealTheme === 'dark' ? 'light' : 'dark');
        return;
      }
      const currencyTrigger = event.target.closest('[data-astreal-currency-trigger]');
      if (currencyTrigger) {
        this.toggleCurrencyPicker(currencyTrigger);
        return;
      }
      const currencyOption = event.target.closest('[data-astreal-currency-option]');
      if (currencyOption) {
        this.selectCurrencyOption(currencyOption);
        return;
      }
      if (!event.target.closest('[data-astreal-currency-picker]')) this.closeCurrencyPickers();
      const faqButton = event.target.closest('[data-astreal-faq-button]');
      if (faqButton) {
        this.toggleFaq(faqButton);
        return;
      }
      const close = event.target.closest('[data-astreal-overlay-close]');
      if (close) {
        this.closeOverlay(close.closest('[data-astreal-overlay]'));
        return;
      }
      const cartTrigger = event.target.closest('[data-astreal-cart-open]');
      if (cartTrigger) {
        event.preventDefault();
        this.openCartDrawer(cartTrigger);
        return;
      }
      const quick = event.target.closest('[data-astreal-quick-add]');
      if (quick) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (quick.matches('a') && (quick.target === '_blank' || quick.hasAttribute('download'))) return;
        event.preventDefault();
        if (event.detail > 0 && quick.closest('[data-astreal-product-rail]') && Date.now() < this.railClickSuppressedUntil) return;
        this.loadQuickAdd(quick);
        return;
      }
      const productSummary = event.target.closest('.astreal-product-disclosure > summary');
      if (productSummary) productSummary.parentElement._astrealReturnTrigger = null;
      const inlineGuide = event.target.closest('[data-astreal-inline-size-guide-open]');
      if (inlineGuide) {
        event.preventDefault();
        this.openInlineSizeGuide(inlineGuide);
        return;
      }
      const guide = event.target.closest('[data-astreal-size-guide-open]');
      if (guide) {
        event.preventDefault();
        this.openSizeGuide(guide);
        return;
      }
      const guideUnit = event.target.closest('[data-astreal-guide-unit]');
      if (guideUnit) {
        this.setGuideUnit(guideUnit);
        return;
      }
      const quickOption = event.target.closest('[data-astreal-quick-option-value]');
      if (quickOption) {
        this.selectQuickOption(quickOption);
        return;
      }
      const cartOptionEditorOpen = event.target.closest('[data-astreal-cart-option-editor-open]');
      if (cartOptionEditorOpen) {
        event.preventDefault();
        this.openCartOptionEditor(cartOptionEditorOpen);
        return;
      }
      const cartEditorOption = event.target.closest('[data-astreal-cart-editor-option-value]');
      if (cartEditorOption) {
        event.preventDefault();
        this.selectCartEditorOption(cartEditorOption);
        return;
      }
      const cartOptionEditorApply = event.target.closest('[data-astreal-cart-option-editor-apply]');
      if (cartOptionEditorApply) {
        event.preventDefault();
        this.applyCartOptionEditor();
        return;
      }
      const productOption = event.target.closest('[data-astreal-option-value]');
      if (productOption) {
        this.selectProductOption(productOption);
        return;
      }
      const gallery = event.target.closest('[data-astreal-gallery-target]');
      if (gallery) {
        this.setGalleryMedia(gallery.closest('[data-astreal-product]'), gallery.dataset.astrealGalleryTarget);
        return;
      }
      const imageViewer = event.target.closest('[data-astreal-image-viewer-open]');
      if (imageViewer) {
        event.preventDefault();
        const galleryStage = imageViewer.closest('[data-astreal-gallery-stage]');
        if (galleryStage?.dataset.astrealGallerySwiped === 'true') {
          delete galleryStage.dataset.astrealGallerySwiped;
          return;
        }
        this.openImageViewer(imageViewer);
        return;
      }
      const minus = event.target.closest('[data-astreal-quantity-minus]');
      if (minus) {
        this.changeQuantity(minus, -1);
        return;
      }
      const plus = event.target.closest('[data-astreal-quantity-plus]');
      if (plus) {
        this.changeQuantity(plus, 1);
        return;
      }
      const cartMinus = event.target.closest('[data-astreal-cart-minus]');
      if (cartMinus) {
        this.stepCartQuantity(cartMinus, -1);
        return;
      }
      const cartPlus = event.target.closest('[data-astreal-cart-plus]');
      if (cartPlus) {
        this.stepCartQuantity(cartPlus, 1);
        return;
      }
      const cartRemove = event.target.closest('[data-astreal-cart-remove]');
      if (cartRemove) {
        event.preventDefault();
        this.changeCartLine(cartRemove.dataset.cartKey, 0);
        return;
      }
      const menuTrigger = event.target.closest(this.selectors.menuOpen);
      if (menuTrigger) this.setMenu(true, menuTrigger);
      if (event.target.closest(this.selectors.menuClose)) this.setMenu(false);
    });

    document.addEventListener('submit', (event) => {
      const contactForm = event.target.closest('[data-astreal-contact-form]');
      if (contactForm) {
        this.saveContactCustomFields(contactForm);
        if (!this.validateContactForm(contactForm)) {
          event.preventDefault();
          return;
        }
      }
      const form = event.target.closest('[data-astreal-quick-form], [data-astreal-product-form]');
      if (!form) return;
      event.preventDefault();
      this.addToCart(form);
    });

    document.addEventListener('change', (event) => {
      const select = event.target.closest(this.selectors.currency);
      if (select) select.form?.submit();
      const autoSubmit = event.target.closest('[data-astreal-auto-submit]');
      if (autoSubmit) {
        document.querySelector('[data-astreal-catalog]')?.setAttribute('aria-busy', 'true');
        autoSubmit.form?.requestSubmit();
      }
      const cartQuantity = event.target.closest('[data-astreal-cart-quantity]');
      if (cartQuantity) {
        const quantity = this.validatedCartQuantity(cartQuantity);
        if (quantity != null) this.changeCartLine(cartQuantity.dataset.cartKey, quantity);
      }
    });

    document.addEventListener('input', (event) => {
      const form = event.target.closest('[data-astreal-contact-form]');
      if (form && event.target.matches('[required]')) this.setContactFieldError(form, event.target);
    });

    document.addEventListener('toggle', (event) => {
      if (event.target.matches?.('.astreal-product-disclosure')) {
        const summary = event.target.querySelector('summary');
        summary?.setAttribute('aria-expanded', String(event.target.open));
        const mark = summary?.querySelector('[aria-hidden="true"]');
        if (mark) mark.textContent = event.target.open ? '−' : '+';
        if (event.target.id) {
          document.querySelectorAll('[data-astreal-inline-size-guide-open]').forEach((trigger) => {
            if (trigger.getAttribute('aria-controls') === event.target.id) trigger.setAttribute('aria-expanded', String(event.target.open));
          });
        }
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (this.handleCurrencyKeydown(event)) return;
      const cartOptionEditorOpen = event.target.closest?.('[data-astreal-cart-option-editor-open]');
      if (cartOptionEditorOpen && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.openCartOptionEditor(cartOptionEditorOpen);
        return;
      }
      const faqButton = event.target.closest?.('[data-astreal-faq-button]');
      if (faqButton && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.toggleFaq(faqButton);
        return;
      }
      const productSummary = event.target.closest?.('.astreal-product-disclosure > summary');
      if (productSummary && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        const disclosure = productSummary.parentElement;
        disclosure._astrealReturnTrigger = null;
        disclosure.open = !disclosure.open;
        return;
      }
      const inlineSizeGuide = event.target.closest?.('[data-astreal-product-size-guide][open]');
      if (event.key === 'Escape' && inlineSizeGuide) {
        event.preventDefault();
        const returnTarget = inlineSizeGuide._astrealReturnTrigger || inlineSizeGuide.querySelector('summary');
        inlineSizeGuide.open = false;
        returnTarget?.focus();
        inlineSizeGuide._astrealReturnTrigger = null;
        return;
      }
      if (event.key === 'Escape' && this.overlayStack.length) {
        event.preventDefault();
        this.closeOverlay();
        return;
      }
      const catalogFilter = event.target.closest?.('[data-astreal-catalog-filter][open]');
      if (event.key === 'Escape' && catalogFilter) {
        event.preventDefault();
        catalogFilter.open = false;
        catalogFilter.querySelector('summary')?.focus();
        return;
      }
      if (this.overlayStack.length) this.trapOverlayFocus(event);
      else {
        if (event.key === 'Escape') this.setMenu(false);
        this.trapMenuFocus(event);
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      this.setTheme(this.getResolvedTheme(), false);
      this.initTickerMotion();
      this.initProductRoots(event.target);
      this.initGuides(event.target);
      this.initFaq(event.target);
      this.initCartVariantOptions(event.target);
      this.initProductRails(event.target);
    });
  },

  init() {
    this.setTheme(this.getResolvedTheme(), false);
    this.initTickerMotion();
    this.initProductRoots();
    this.initGuides();
    this.bind();
    this.initPrivacyButtonOrder();
    this.initContactForm();
    this.initFaq();
    this.initCartVariantOptions();
    this.initProductRails();
    const productSearchRedirect = document.querySelector('[data-astreal-product-search-redirect]');
    if (productSearchRedirect) window.location.replace(productSearchRedirect.href);
  },
};

Astreal.init();
window.Astreal = Astreal;
